import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/shared/components/Layout';
import { api } from '@/shared/lib/api';
import { checkAnswer } from '@2enbota/shared';
import { useTTS } from '@/shared/hooks/useTTS';
import { useIsDesktop } from '@/shared/hooks/useIsDesktop';
import { useKeyboardScroll } from '@/shared/hooks/useKeyboardScroll';
import { GiftModal } from '@/features/gamification/GiftModal';
import { trackEvent, captureError } from '@/shared/lib/monitoring';
import { useRpgStore } from '@/stores/rpgStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { InputModeView } from '@/features/study/InputModeView';
import { ComboBadge, RpgProgressBar, EmptySrsView, DailyGoalVictoryWidget, SmartInputFeedback } from '@/features/study/StudyJuice';
import { AdminEditButton } from '@/features/study/AdminEditButton';
import { AiChatBot } from '@/features/ai/AiChatBot';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';
import { useGuidedTourStore } from '@/features/guided-tour';
import { renderHighlightedSentence, renderBlankedSentence } from '@/shared/lib/highlight';
import { playSuccessSound, playErrorSound } from '@/shared/lib/soundEffects';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';
import { useOnTabActive, useIsTabActive } from '@/shared/components/MainTabsKeepAlive';

// =====================
// Types
// =====================

interface StudyWord {
  id: number;
  original_text: string;
  main_translation_text: string;
  srs_level: number;
  mc_en_ru_progress: number;
  mc_ru_en_progress: number;
  input_lives_left: number;
  error_count: number;
  error_streak: number;
  translations: Array<{ translation_text: string }>;
  examples: Array<{ example_sentence: string; example_translation: string }>;
  // Word metadata
  part_of_speech?: string | null;
  cefr_level?: string | null;
  transcription?: string | null;
  russian_phonetic?: string | null;
  is_phrase?: boolean;
  plural_form?: string | null;
}

interface StudyCard {
  word: StudyWord;
  mode: string;
  direction: string;
  options?: string[];
  correct_answer?: string;
  required_mc_reps?: number;
  hint?: {
    context: string;
    example: string;
    hint_level: string;
    difficulty_indicator: string | null;
    error_count: number;
    error_streak: number;
  };
  difficulty?: string;
  // Контекстное предложение с blanked словом (из dictionary_examples)
  context_sentence?: {
    en: string;
    en_blanked: string;
    ru: string;
    ru_blanked: string;
  };
  // Поля для режима blanked:
  sentence_en?: string;
  sentence_en_blanked?: string;
  sentence_ru?: string;
  sentence_ru_blanked?: string;
  // Поля для режима phrases:
  phrase_en?: string;
  phrase_ru?: string;
  // Для RU→EN направления:
  prompt_text?: string;      // Что произносить/показывать (русский)
  expected_answer?: string;  // Правильный ответ (английский)
}

interface StudySession {
  cards: StudyCard[];
  total: number;
  mode: string;
  has_more?: boolean;
  total_eligible?: number;
  offset?: number;
}

interface AiFeedback {
  error_type: string;
  hint: string;
  corrected_suggestion?: string;
  should_spare_life?: boolean;
}

interface AnswerResult {
  word_id: number;
  is_correct: boolean;
  new_srs_level: number;
  new_mc_en_ru_progress: number;
  new_mc_ru_en_progress: number;
  transitioned_to_input: boolean;
  word_reset_to_mc?: boolean;
  xp_gained: number;
  loot_drop?: { item: { name: string; description: string; rarity: string; slot: string; flavor_text: string }; rarity: string };
  awaiting_retry?: boolean;
  ai_feedback?: AiFeedback;
}

// =====================
// Mode Selector
// =====================

function ModeSelector({ onSelectMode, wordCounts, courseMode, courseName, folderMode, folderName, onNavigate }: {
  onSelectMode: (mode: string) => void;
  wordCounts: { total: number; in_mc: number; in_input: number; in_srs: number; due_now: number; phrases?: number; phrases_in_mc?: number; completed?: number } | null;
  courseMode?: boolean;
  courseName?: string | null;
  folderMode?: boolean;
  folderName?: string | null;
  onNavigate?: (path: string) => void;
}) {
  const inMc = wordCounts?.in_mc ?? 0;
  const inInput = wordCounts?.in_input ?? 0;
  const inSrs = wordCounts?.in_srs ?? 0;
  const dueNow = wordCounts?.due_now ?? 0;
  const hasAny = (wordCounts?.total ?? 0) > 0;

  const coreSteps = [
    {
      key: 'mc',
      label: courseMode ? 'MC — Выбор ответа' : 'Изучение',
      iconUrl: '/icons/modes/mc.png',
      description: 'Выбор правильного ответа',
      countLabel: `${inMc} слов`,
      disabled: courseMode ? inMc === 0 : !hasAny,
      accentColor: 'var(--mode-mc, var(--theme-primary))',
      badgeBg: 'rgba(124, 77, 255, 0.15)',
      badgeColor: 'var(--theme-primary)',
    },
    {
      key: 'input',
      label: courseMode ? 'Ввод слов' : 'Ввод слова',
      iconUrl: '/icons/modes/input.png',
      description: 'Пиши по памяти',
      countLabel: `${inInput} слов`,
      disabled: inInput === 0,
      accentColor: 'var(--mode-input, #00e5ff)',
      badgeBg: 'rgba(0, 229, 255, 0.14)',
      badgeColor: 'var(--mode-input, #00e5ff)',
    },
    {
      key: 'srs',
      label: 'Повторение',
      iconUrl: '/icons/modes/srs.png',
      description: 'Интервалы, чтобы не забыть',
      countLabel: dueNow > 0 ? `${dueNow} к повтору` : (inSrs > 0 ? 'Все повторены ✓' : '0 слов'),
      disabled: dueNow === 0,
      accentColor: 'var(--mode-srs, #ff6b35)',
      badgeBg: 'rgba(255, 107, 53, 0.16)',
      badgeColor: 'var(--mode-srs, #ff6b35)',
    },
  ];

  const extraDrills = [
    { key: 'audition', label: 'Аудирование', sub: 'Выбор на слух', iconUrl: '/icons/modes/audition.png', color: 'var(--mode-audition, #ff4081)', disabled: !hasAny },
    { key: 'dictation', label: 'Диктант', sub: 'Пиши на слух', iconUrl: '/icons/modes/dictation.png', color: 'var(--mode-dictation, #ffb300)', disabled: !hasAny },
    { key: 'phrases', label: 'Фразы', sub: 'В контексте', iconUrl: '/icons/modes/phrases.png', color: 'var(--theme-primary)', disabled: !hasAny },
    { key: 'blanked', label: 'Пропуски', sub: 'Вставь слово', iconUrl: '/icons/modes/blanked.png', color: 'var(--theme-primary)', disabled: !hasAny },
  ];

  return (
    <div style={{ padding: '20px 16px' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 20, color: 'var(--theme-text)' }}>
        {folderMode && folderName ? folderName : courseMode && courseName ? courseName : 'Учёба'}
      </h1>
      {courseMode ? (
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
          Изучение курса · {wordCounts?.total ?? 0} слов · {wordCounts?.completed ?? 0} завершено
        </p>
      ) : folderMode ? (
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
          📁 Папка · {wordCounts?.total ?? 0} слов
        </p>
      ) : wordCounts && (
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
          Всего слов: {wordCounts.total}
          {wordCounts.total === 0 && ' — добавьте слова для начала учёбы'}
        </p>
      )}

      {/* Section Header */}
      <div style={{
        fontSize: 12,
        fontWeight: 800,
        color: 'var(--theme-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        margin: '0 0 10px 2px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        {courseMode ? '📚 Шаги прохождения урока' : '🔥 Основной цикл обучения'}
      </div>

      {/* Core Learning Cards (Variant 3: Left Accent Strip + Fluent Color Icons) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
        {coreSteps.map(step => (
          <button
            key={step.key}
            type="button"
            onClick={() => !step.disabled && onSelectMode(step.key)}
            disabled={step.disabled}
            data-tour-target={step.key === 'mc' ? 'select-mc' : undefined}
            style={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '14px 14px 14px 18px',
              background: 'var(--theme-bg-card)',
              border: '1px solid var(--theme-border)',
              borderRadius: 14,
              color: step.disabled ? 'var(--theme-text-muted)' : 'var(--theme-text)',
              cursor: step.disabled ? 'not-allowed' : 'pointer',
              textAlign: 'left',
              width: '100%',
              opacity: step.disabled ? 0.5 : 1,
              transition: 'border-color 0.2s',
            }}
          >
            {/* Left Accent Strip */}
            <div style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 5,
              background: step.accentColor,
            }} />

            <img
              src={step.iconUrl}
              alt={step.label}
              style={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--theme-text)' }}>
                {step.label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 2, lineHeight: 1.3 }}>
                {step.description}
              </div>
            </div>
            <div style={{
              padding: '4px 9px',
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 800,
              background: step.badgeBg,
              color: step.badgeColor,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}>
              {step.countLabel}
            </div>
          </button>
        ))}
      </div>

      {/* Extra Drills (2x2 Grid with Fluent Color Icons) */}
      {!courseMode && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 2px 10px',
          }}>
            <span style={{
              fontSize: 12,
              fontWeight: 800,
              color: 'var(--theme-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}>
              Дополнительные тренировки
            </span>
            <span style={{
              fontSize: 11,
              color: 'var(--theme-text-muted)',
              fontWeight: 600,
            }}>
              4 режима
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 10,
          }}>
            {extraDrills.map(drill => (
              <button
                key={drill.key}
                type="button"
                onClick={() => !drill.disabled && onSelectMode(drill.key)}
                disabled={drill.disabled}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '12px 14px',
                  background: 'var(--theme-bg-card)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 14,
                  color: drill.disabled ? 'var(--theme-text-muted)' : 'var(--theme-text)',
                  cursor: drill.disabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  opacity: drill.disabled ? 0.5 : 1,
                  transition: 'border-color 0.2s',
                }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <img
                    src={drill.iconUrl}
                    alt={drill.label}
                    style={{ width: 28, height: 28, objectFit: 'contain' }}
                  />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--theme-text)' }}>
                    {drill.label}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                    {drill.sub}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!courseMode && !folderMode && wordCounts?.total === 0 && (
        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            onClick={() => onNavigate?.('/words')}
            style={{
              width: '100%', padding: '14px',
              background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
              border: 'none', borderRadius: 12,
              fontSize: 15, fontWeight: 600, cursor: 'pointer',
            }}
          >
            📚 Перейти к словам
          </button>
          <button
            type="button"
            onClick={() => onNavigate?.('/words?add=true')}
            style={{
              width: '100%', padding: '14px',
              background: 'var(--theme-bg-card)', color: 'var(--theme-text)',
              border: '1px solid var(--theme-border)', borderRadius: 12,
              fontSize: 15, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ✍️ Добавить своё слово
          </button>
        </div>
      )}
    </div>
  );
}

// =====================
// Toast notification
// =====================

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed',
      bottom: 80,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'var(--theme-success)',
      color: '#fff',
      padding: '10px 20px',
      borderRadius: 24,
      fontSize: 14,
      fontWeight: 600,
      zIndex: 1000,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      animation: 'toast-in 0.3s ease',
    }}>
      {message}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// =====================
// MC Card
// =====================

// Словари для тегов части речи и уровня CEFR
const POS_INFO: Record<string, { en: string; ru: string; description: string; examples: string }> = {
  noun:         { en: 'noun',         ru: 'существительное', description: 'Обозначает предмет, лицо, место или понятие. Отвечает на «кто?» и «что?».', examples: 'dog, happiness, London' },
  verb:         { en: 'verb',         ru: 'глагол',          description: 'Обозначает действие или состояние.',                                          examples: 'run, think, become' },
  adjective:    { en: 'adjective',    ru: 'прилагательное',  description: 'Описывает признак или качество предмета.',                                    examples: 'big, beautiful, fast' },
  adverb:       { en: 'adverb',       ru: 'наречие',         description: 'Описывает как, где, когда происходит действие.',                               examples: 'quickly, very, here' },
  preposition:  { en: 'preposition',  ru: 'предлог',         description: 'Показывает отношение между словами.',                                          examples: 'in, on, at, for' },
  conjunction:  { en: 'conjunction',  ru: 'союз',            description: 'Соединяет слова или части предложения.',                                       examples: 'and, but, because' },
  pronoun:      { en: 'pronoun',      ru: 'местоимение',     description: 'Заменяет существительное.',                                                    examples: 'he, she, it, they' },
  interjection: { en: 'interjection', ru: 'междометие',      description: 'Выражает эмоцию или реакцию.',                                                 examples: 'oh, wow, oops' },
};

const POS_COLORS: Record<string, string> = {
  noun: '#378ADD', verb: '#639922', adjective: '#7F77DD', adverb: '#1D9E75',
  preposition: '#888780', conjunction: '#D85A30', pronoun: '#D4537E', interjection: '#EF9F27',
};

const CEFR_INFO: Record<string, { title: string; description: string }> = {
  A1: { title: 'Beginner',           description: 'Начальный — базовые слова и фразы.' },
  A2: { title: 'Elementary',         description: 'Элементарный — простые повседневные выражения.' },
  B1: { title: 'Intermediate',       description: 'Средний — понятные тексты на знакомые темы.' },
  B2: { title: 'Upper-Intermediate', description: 'Выше среднего — свободное общение на широкие темы.' },
  C1: { title: 'Advanced',           description: 'Продвинутый — сложные тексты, тонкие нюансы.' },
  C2: { title: 'Proficiency',        description: 'Владение в совершенстве — уровень носителя.' },
};

// ===== Ударение из IPA =====
const IPA_VOWELS = new Set('aɑæʌɒɔəɛɜɪiɵʊuːeɝoʉ');
const EN_VOWELS = new Set('aeiouyAEIOUY');

// Находит индекс ударной гласной в английском слове по IPA транскрипции
function getStressIndex(word: string, ipa: string): number {
  if (!ipa || !word) return -1;
  // Находим позицию ˈ в IPA
  const stressPos = ipa.indexOf('ˈ');
  // Если нет ˈ (односложное слово) — ударение на первую гласную
  if (stressPos === -1) {
    for (let i = 0; i < word.length; i++) {
      if (EN_VOWELS.has(word[i])) return i;
    }
    return -1;
  }
  // Считаем, какая по счёту гласная IPA идёт после ˈ
  let ipaVowelIdx = 0;
  for (let i = 0; i < stressPos; i++) {
    if (IPA_VOWELS.has(ipa[i])) ipaVowelIdx++;
  }
  // Находим ту же по счёту гласную в слове
  let count = 0;
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) {
      if (count === ipaVowelIdx) return i;
      count++;
    }
  }
  // Fallback — первая гласная
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) return i;
  }
  return -1;
}

// Вставляет ударение в слово/фразу (на основе IPA)
function addStressMark(word: string, ipa: string): string {
  // Фраза из нескольких слов — обрабатываем каждое
  const words = word.split(/(\s+|-)/); // разбиваем по пробелам и дефисам, сохраняя разделители
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/); // IPA части (без точек)
  let ipaIdx = 0;
  return words.map(part => {
    // Разделители (пробел, дефис) — пропускаем
    if (/^[\s-]+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    const idx = getStressIndex(part, partIpa);
    if (idx < 0) return part;
    return part.slice(0, idx + 1) + '\u0301' + part.slice(idx + 1);
  }).join('');
}

// Вставляет ударение в русскую фонетику (на основе IPA), поддерживает фразы
function addPhoneticStress(phonetic: string, ipa: string): string {
  if (!phonetic || !ipa) return phonetic;
  const cleanPh = phonetic.replace(/\u0301/g, '');
  const RU_VOWELS = new Set('аеёиоуыэюяАЕЁИОУЫЭЮЯ');

  const addStressToSingle = (ph: string, partIpa: string): string => {
    if (!ph) return ph;
    const pos = partIpa.indexOf('ˈ');
    let targetVowelIdx = 0;
    if (pos !== -1) {
      for (let i = 0; i < pos; i++) {
        if (IPA_VOWELS.has(partIpa[i])) targetVowelIdx++;
      }
    }

    // Собираем слоты ударных гласных (учитывая, что "у" перед гласной — это согласный glide W,
    // а в дифтонгах типа "ау", "оу" ударение падает на первую гласную)
    const vowelSlots: number[] = [];
    let i = 0;
    while (i < ph.length) {
      const ch = ph[i].toLowerCase();
      if (RU_VOWELS.has(ch)) {
        if (ch === 'у' && i + 1 < ph.length && RU_VOWELS.has(ph[i + 1].toLowerCase())) {
          vowelSlots.push(i + 1);
          i += 2;
          continue;
        } else if ('аоэ'.includes(ch) && i + 1 < ph.length && ph[i + 1].toLowerCase() === 'у') {
          vowelSlots.push(i);
          i += 2;
          continue;
        } else {
          vowelSlots.push(i);
          i += 1;
        }
      } else {
        i += 1;
      }
    }

    if (vowelSlots.length === 0) return ph;
    const stressIdx = targetVowelIdx < vowelSlots.length ? vowelSlots[targetVowelIdx] : vowelSlots[0];
    return ph.slice(0, stressIdx + 1) + '\u0301' + ph.slice(stressIdx + 1);
  };

  const phParts = cleanPh.split(/(\s+)/);
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/).filter(Boolean);
  let ipaIdx = 0;
  return phParts.map(part => {
    if (/^\s+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    return addStressToSingle(part, partIpa);
  }).join('');
}

// ===== Простой markdown → HTML =====
function simpleMarkdown(text: unknown): string {
  const safe = typeof text === 'string'
    ? text
    : text == null
      ? ''
      : (() => { try { return JSON.stringify(text); } catch { return String(text); } })();
  // Экранируем HTML-теги ПЕРЕД markdown-заменами (XSS защита)
  const escaped = safe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped
    .replace(/### (.+)/g, '<div style="font-size:13px;font-weight:600;margin:10px 0 4px;color:var(--theme-text)">$1</div>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--theme-primary)">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:rgba(139,92,246,0.15);padding:1px 5px;border-radius:4px;font-size:12px;color:var(--theme-primary)">$1</code>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, ' ');
}

// ===== ExplanationView — рендер новой структурированной подсказки от 2EnBota =====
type ExplanationData = {
  greeting?: unknown;
  meaning?: unknown;
  memory_hook?: unknown;
  examples?: unknown;
  explanation?: unknown; // legacy
};

function ExplanationView({ data }: { data: ExplanationData }) {
  const toStr = (v: unknown): string => {
    if (typeof v === 'string') return v.trim();
    if (v == null) return '';
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  const greeting = toStr(data.greeting);
  const meaning = toStr(data.meaning) || toStr(data.explanation);
  const memoryHook = toStr(data.memory_hook);
  const examples = Array.isArray(data.examples) ? data.examples : [];

  if (!greeting && !meaning && !memoryHook && examples.length === 0) {
    return <div style={{ color: 'var(--theme-text-muted)' }}>🦊🦝 Не удалось загрузить</div>;
  }

  const sectionStyle = { marginTop: 14 };
  const headerStyle = { fontSize: 11.5, color: 'var(--theme-text-muted)', marginBottom: 6, fontWeight: 700 as const, letterSpacing: 0.5 };
  const bodyStyle = { color: 'var(--theme-text)', lineHeight: 1.6, fontSize: 14.5 };

  return (
    <div>
      {greeting && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
          <span style={{ fontSize: 18, lineHeight: 1.2, flexShrink: 0 }}>🦊🦝</span>
          <div style={{ ...bodyStyle, fontStyle: 'italic' }} dangerouslySetInnerHTML={{ __html: simpleMarkdown(greeting) }} />
        </div>
      )}
      {meaning && (
        <div style={sectionStyle}>
          <div style={headerStyle}>📖 ЧТО ЗНАЧИТ</div>
          <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: simpleMarkdown(meaning) }} />
        </div>
      )}
      {memoryHook && (
        <div style={sectionStyle}>
          <div style={headerStyle}>🧠 КАК ЗАПОМНИТЬ</div>
          <div style={bodyStyle} dangerouslySetInnerHTML={{ __html: simpleMarkdown(memoryHook) }} />
        </div>
      )}
      {examples.length > 0 && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--theme-border)', paddingTop: 12 }}>
          <div style={headerStyle}>💬 ПРИМЕРЫ</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {examples.map((ex: any, i: number) => (
              <div
                key={i}
                style={{
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.035)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 10,
                }}
              >
                <div style={{ color: 'var(--theme-text)', fontSize: 14.5, fontWeight: 500, lineHeight: 1.4 }}>
                  {toStr(ex?.original || ex?.en || ex?.sentence)}
                </div>
                {ex?.phonetic && (
                  <div style={{ color: '#c084fc', fontSize: 12.5, fontStyle: 'italic', marginTop: 3 }}>
                    {toStr(ex.phonetic)}
                  </div>
                )}
                <div style={{ color: 'var(--theme-text-secondary)', fontSize: 13.5, lineHeight: 1.4, marginTop: 4 }}>
                  → {toStr(ex?.translation || ex?.ru)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== AiHintBlock — подсказка от 2EnBota 🦊🦝 =====
function AiHintBlock({ card, difficultyColor }: { card: StudyCard; difficultyColor: string | null }) {
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestedRef = useRef<string>('');

  // При смене карточки — запрашиваем AI hint
  useEffect(() => {
    // Реальные значения для конкретной MC-карточки:
    // - в EN→RU question = original_text (EN), correct = main или альт (RU)
    // - в RU→EN question = main или альт (RU), correct = original_text (EN)
    // Подсказка про конкретную пару (вопрос, ответ) этой карточки,
    // а не про главный перевод слова.
    const isEnDirection = card.direction === 'original_to_translation';
    const mcQuestion = (card.word as any)._mc_question as string | undefined;
    const enWord = isEnDirection
      ? card.word.original_text
      : (card.correct_answer ?? card.word.original_text);
    const ruWord = isEnDirection
      ? (card.correct_answer ?? card.word.main_translation_text)
      : (mcQuestion ?? card.word.main_translation_text);

    const key = `${card.word.id}-${card.direction}-${enWord}-${ruWord}`;
    if (requestedRef.current === key) return;
    requestedRef.current = key;
    setAiMessage(null);
    setIsLoading(true);

    api.post<{ message: string }>('/ai/mc-hint', {
      word: enWord,
      translation: ruWord,
      direction: card.direction,
      hiddenWord: card.hint?.example || '',
      errorStreak: card.hint?.error_streak || 0,
      errorCount: card.hint?.error_count || 0,
    }).then(result => {
      setAiMessage(result.message);
    }).catch(() => {
      setAiMessage((card.hint as any)?.message || '🦊🦝 Не сдавайся!');
    }).finally(() => {
      setIsLoading(false);
    });
  }, [card.word.id, card.direction, card.correct_answer, (card.word as any)._mc_question]);

  return (
    <div style={{
      marginBottom: 12,
      padding: '12px 14px',
      background: 'var(--theme-bg-elevated)',
      borderRadius: 12,
      borderLeft: `3px solid ${difficultyColor || 'var(--theme-primary)'}`,
      fontSize: 13,
    }}>
      <div style={{ color: 'var(--theme-text)', fontWeight: 500, lineHeight: 1.5 }}>
        {isLoading ? (
          <span style={{ color: 'var(--theme-text-muted)' }}>🦊🦝 Думаем...</span>
        ) : (
          aiMessage || '🦊🦝 Подсказка...'
        )}
      </div>
    </div>
  );
}

// ===== ExampleBlock — контейнер примера под карточкой =====
const ExTranslateIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 8l6 6" /><path d="M4 14l6-6 2-3" /><path d="M2 5h12" /><path d="M7 2h1" />
    <path d="m22 22-5-10-5 10" /><path d="M14 18h6" />
  </svg>
);
const ExChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);
const ExSparkleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
    <path d="M19 15l.6 2.4L22 18l-2.4.6L19 21l-.6-2.4L16 18l2.4-.6L19 15z" />
  </svg>
);

const GENERATING_PHRASES: Array<{ icon: string; text: string }> = [
  { icon: '✨', text: '2EnBota придумывает новый пример...' },
  { icon: '✨', text: 'Колдуем над новым предложением...' },
  { icon: '🦝', text: 'Сочиняем пример из реальной жизни...' },
  { icon: '🦊', text: 'Подбираем классный пример...' },
  { icon: '✨', text: 'Создаём предложение с этим словом...' },
  { icon: '🦝', text: 'Еноты уже пишут новый пример...' },
  { icon: '🦊', text: 'Генерируем ваше предложение...' },
];

function ExampleBlock({ examples, wordOriginal, wordTranslation, direction, isAnswered }: {
  examples: Array<{ en: string; en_blanked: string; ru: string; ru_blanked: string }>;
  wordOriginal: string;
  wordTranslation: string;
  direction?: string;
  isAnswered?: boolean;
}) {
  const [showTranslation, setShowTranslation] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingPhrase, setLoadingPhrase] = useState(GENERATING_PHRASES[0]);
  const [exIndex, setExIndex] = useState(0);
  const [extraExamples, setExtraExamples] = useState<Array<{ en?: string; en_blanked: string; ru: string }>>([]);
  const [history, setHistory] = useState<string[]>([]);

  const allExamples = [
    ...examples.map(e => ({ en: e.en, en_blanked: e.en_blanked, ru: e.ru })),
    ...extraExamples,
  ];
  const current = allExamples[exIndex] || allExamples[0];

  // Сбрасываем при смене слова
  useEffect(() => {
    setShowTranslation(false);
    setExIndex(0);
    setExtraExamples([]);
    setHistory(examples.map(e => e.en_blanked));
  }, [examples]);

  // Если направление EN -> RU или уже дан ответ, показываем слово целиком с цветной подсветкой
  const isEnDirection = direction === 'original_to_translation';
  const shouldShowFull = isEnDirection || isAnswered;

  // Кнопка «Ещё» — листает по кругу
  const handleNext = () => {
    if (allExamples.length <= 1) return;
    setIsSwapping(true);
    setShowTranslation(false);
    setTimeout(() => {
      setExIndex(prev => (prev + 1) % allExamples.length);
      setIsSwapping(false);
    }, 280);
  };

  // Кнопка AI генерации
  const handleGenerate = async () => {
    if (isGenerating) return;
    setLoadingPhrase(GENERATING_PHRASES[Math.floor(Math.random() * GENERATING_PHRASES.length)]);
    setIsGenerating(true);
    setShowTranslation(false);
    try {
      const result = await api.post<{ examples: Array<{ sentence: string; sentence_blanked: string; translation: string }> }>(
        '/ai/generate-example',
        { word: wordOriginal, translation: wordTranslation, previous_examples: history },
      );
      const ex = result.examples?.[0];
      if (ex) {
        const newItem = { en: ex.sentence, en_blanked: ex.sentence_blanked, ru: ex.translation };
        setIsSwapping(true);
        setTimeout(() => {
          setExtraExamples(prev => {
            const updated = [...prev, newItem];
            setExIndex(examples.length + updated.length - 1); // переключаемся на новый
            return updated;
          });
          setHistory(prev => [...prev, ex.sentence_blanked]);
          setIsSwapping(false);
        }, 200);
      }
    } catch {
      // ignore
    } finally {
      setIsGenerating(false);
    }
  };

  const renderSentenceContent = () => {
    if (isGenerating) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#c084fc', fontSize: 13.5, fontStyle: 'normal' }}>
          <span style={{ display: 'inline-block', animation: 'spin 1.2s linear infinite' }}>{loadingPhrase.icon}</span>
          <span style={{ fontWeight: 500 }}>{loadingPhrase.text}</span>
        </div>
      );
    }

    if (shouldShowFull) {
      let sentence = current?.en || '';
      if (!sentence || /_{3,}/.test(sentence)) {
        sentence = (current?.en_blanked || '').replace(/_{3,}/g, wordOriginal);
      }
      if (!sentence) sentence = current?.en_blanked || '';
      return renderHighlightedSentence(sentence, wordOriginal);
    }

    const sentenceToMask = current?.en_blanked || current?.en || '';
    return renderBlankedSentence(sentenceToMask, wordOriginal);
  };

  return (
    <div style={{
      width: '100%', maxWidth: 400,
      display: 'flex',
      borderRadius: 14,
      overflow: 'hidden',
      background: 'var(--theme-bg-elevated)',
      border: '1px solid var(--theme-border)',
      marginTop: 10, marginBottom: 12,
    }}>
      {/* Градиентная полоска */}
      <div style={{
        width: 4, flexShrink: 0,
        background: 'linear-gradient(to bottom, #2196F3, #8B5CF6, #EC4899)',
      }} />

      <div style={{ flex: 1, padding: '14px 18px' }}>
        {/* Предложение с подсветкой или пропуском */}
        <div style={{
          fontSize: 15, fontStyle: 'italic',
          color: 'var(--theme-text-secondary)',
          lineHeight: 1.6, minHeight: 24,
          transition: 'opacity 0.25s, transform 0.25s',
          opacity: isSwapping ? 0 : 1,
          transform: isSwapping ? 'translateY(-6px)' : 'translateY(0)',
        }}>
          {renderSentenceContent()}
        </div>

        {/* Перевод (скрытый по умолчанию) */}
        <div style={{
          overflow: 'hidden',
          maxHeight: showTranslation && !isSwapping ? 50 : 0,
          opacity: showTranslation && !isSwapping ? 1 : 0,
          transition: 'max-height 0.35s cubic-bezier(0.16,1,0.3,1), opacity 0.3s ease, margin 0.3s ease',
          marginTop: showTranslation && !isSwapping ? 10 : 0,
        }}>
          <div style={{ height: 1, background: 'var(--theme-border)', marginBottom: 10 }} />
          <div style={{ fontSize: 13.5, color: '#c084fc', animation: 'slideReveal 0.3s ease' }}>
            {current?.ru}
          </div>
        </div>

        {/* Кнопки действий */}
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center',
          marginTop: 12,
          borderTop: '1px solid var(--theme-border)',
          paddingTop: 12,
        }}>
          {/* Перевод */}
          <button
            onClick={() => setShowTranslation(v => !v)}
            style={{
              flex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '9px 0', borderRadius: 10,
              border: showTranslation ? '1px solid rgba(139,92,246,0.35)' : '1px solid var(--theme-border)',
              background: showTranslation ? 'rgba(139,92,246,0.12)' : 'var(--theme-bg-card)',
              color: showTranslation ? '#c084fc' : 'var(--theme-text-muted)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <ExTranslateIcon />
            {showTranslation ? 'Скрыть' : 'Перевод'}
          </button>

          {/* Ещё (следующий из пула) */}
          {allExamples.length > 1 && (
          <button
            onClick={handleNext}
            style={{
              flex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '9px 0', borderRadius: 10,
              border: '1px solid var(--theme-border)',
              background: 'var(--theme-bg-card)',
              color: 'var(--theme-text-muted)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <ExChevronIcon />
            Ещё
          </button>
          )}

          {/* AI генерация */}
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            style={{
              width: 38, height: 38, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 10,
              border: '1px solid rgba(236,72,153,0.25)',
              background: isGenerating ? 'rgba(236,72,153,0.15)' : 'rgba(236,72,153,0.08)',
              color: 'rgba(236,72,153,0.75)',
              cursor: isGenerating ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isGenerating ? 0.6 : 1,
            }}
          >
            <ExSparkleIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

// Фиксированные параметры конфетти (без Math.random — стабильный рендер)
const CONFETTI = [
  { left: 8,  delay: 0,    dur: 0.55, shape: 'circle',  color: 'var(--theme-primary)' },
  { left: 18, delay: 0.05, dur: 0.65, shape: 'square',  color: 'var(--theme-success)' },
  { left: 28, delay: 0.02, dur: 0.6,  shape: 'circle',  color: 'var(--theme-warning)' },
  { left: 38, delay: 0.08, dur: 0.5,  shape: 'square',  color: 'var(--theme-primary)' },
  { left: 48, delay: 0.03, dur: 0.7,  shape: 'circle',  color: 'var(--theme-success)' },
  { left: 58, delay: 0.06, dur: 0.55, shape: 'square',  color: 'var(--theme-warning)' },
  { left: 68, delay: 0.01, dur: 0.65, shape: 'circle',  color: 'var(--theme-primary)' },
  { left: 78, delay: 0.07, dur: 0.6,  shape: 'square',  color: 'var(--theme-success)' },
  { left: 88, delay: 0.04, dur: 0.5,  shape: 'circle',  color: 'var(--theme-warning)' },
];

function McCard({ card, onAnswer, onManualAdvance, mcReps = 1, autoAdvance = true, isMastered = false, isDesktop = false, onWordEdited, combo = 0 }: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean) => void;
  onManualAdvance?: () => void;
  mcReps?: number;
  autoAdvance?: boolean;
  isMastered?: boolean;
  isDesktop?: boolean;
  onWordEdited?: (updated?: any) => void;
  combo?: number;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [explanation, setExplanation] = useState<any>(null);
  const [isLoadingExplain, setIsLoadingExplain] = useState(false);
  const [openPopup, setOpenPopup] = useState<'pos' | 'cefr' | null>(null);

  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIsCorrectRef = useRef<boolean | null>(null);
  const cefrBadgeRef = useRef<HTMLSpanElement>(null);

  const { speak } = useTTS();
  const { notify, selection } = useHapticFeedback();

  const isEnDirection = card.direction === 'original_to_translation';

  // Бэкенд может передать альтернативный перевод как правильный ответ
  const correctAnswer = card.correct_answer ?? (isEnDirection
    ? card.word.main_translation_text
    : card.word.original_text);

  const question = isEnDirection
    ? card.word.original_text
    : ((card.word as any)._mc_question ?? card.word.main_translation_text);

  const handleSelect = (option: string) => {
    if (revealed) return;
    selection();
    setSelected(option);
    setRevealed(true);
    const isCorrect = option === correctAnswer;
    notify(isCorrect ? 'success' : 'error');
    if (isCorrect) {
      playSuccessSound(combo + 1);
    } else {
      playErrorSound();
    }
    trackEvent('mc_answer_selected', {
      word_id: card.word.id,
      word: card.word.original_text,
      mode: card.mode,
      direction: card.direction,
      question,
      correct_answer: correctAnswer,
      selected: option,
      is_correct: isCorrect,
      options: card.options,
      lives_left: card.word.input_lives_left,
      srs_level: card.word.srs_level,
    });
    if (autoAdvance) {
      pendingIsCorrectRef.current = isCorrect;
      autoAdvanceTimerRef.current = setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        pendingIsCorrectRef.current = null;
        onAnswer(isCorrect);
        setSelected(null);
        setRevealed(false);
      }, 800);
    } else {
      // Показываем фидбек, вызываем onAnswer, но НЕ сбрасываем состояние
      setTimeout(() => {
        onAnswer(isCorrect);
        setAnswerSubmitted(true);
      }, 600);
    }
  };

  const handleTtsClick = () => {
    if (isEnDirection || revealed) {
      speak(card.word.original_text, 'en-US');
    }
  };

  const handleExplain = async () => {
    if (isLoadingExplain) return;
    if (showExplain) { setShowExplain(false); return; }
    // Отменяем авто-переход если он ещё не сработал
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    setShowExplain(true);
    setIsLoadingExplain(true);
    try {
      // Реальная (вопрос, ответ) пара этой MC-карточки.
      // EN→RU: question = original_text, correct = main_translation или альт.
      // RU→EN: question = _mc_question или main_translation, correct = original_text.
      const isEnDir = card.direction === 'original_to_translation';
      const mcQ = (card.word as any)._mc_question as string | undefined;
      const enWord = isEnDir
        ? card.word.original_text
        : (card.correct_answer ?? card.word.original_text);
      const ruWord = isEnDir
        ? (card.correct_answer ?? card.word.main_translation_text)
        : (mcQ ?? card.word.main_translation_text);
      const result = await api.post('/ai/explain-word', {
        word: enWord,
        translation: ruWord,
      });
      setExplanation(result);
    } catch {
      setExplanation({ meaning: '🦊🦝 Не удалось загрузить объяснение, попробуй ещё раз!' });
    } finally {
      setIsLoadingExplain(false);
    }
  };

  // Авто-переход отменён через explain — вызываем onAnswer вручную
  const handleManualAdvanceWithAnswer = () => {
    if (pendingIsCorrectRef.current !== null) {
      onAnswer(pendingIsCorrectRef.current);
      pendingIsCorrectRef.current = null;
    } else {
      onManualAdvance?.();
    }
  };

  const getDifficultyColor = () => {
    switch (card.difficulty) {
      case 'nightmare': return 'var(--theme-error)';
      case 'hard': return '#ff6b35';
      case 'current_problem': return 'var(--theme-warning)';
      case 'medium': return 'var(--theme-primary)';
      default: return null;
    }
  };

  const difficultyColor = getDifficultyColor();

  const requiredReps = card.required_mc_reps ?? mcReps;
  const enRuDone = card.word.mc_en_ru_progress >= requiredReps;
  const ruEnDone = card.word.mc_ru_en_progress >= requiredReps;

  // Ref для актуальных значений внутри keyboard handler
  const kbRef = useRef({ revealed, autoAdvance, answerSubmitted, showExplain, isLoadingExplain, options: card.options || [], onManualAdvance, handleSelect, handleExplain, handleManualAdvanceWithAnswer, speak });
  kbRef.current = { revealed, autoAdvance, answerSubmitted, showExplain, isLoadingExplain, options: card.options || [], onManualAdvance, handleSelect, handleExplain, handleManualAdvanceWithAnswer, speak };

  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      const { revealed, autoAdvance, answerSubmitted, showExplain, isLoadingExplain, options, onManualAdvance, handleSelect, handleExplain, handleManualAdvanceWithAnswer, speak } = kbRef.current;
      // Shift+2 — озвучить слово (e.key при Shift будет '@', проверяем e.code)
      if (e.shiftKey && (e.key === '2' || e.code === 'Digit2' || e.code === 'Numpad2')) {
        e.preventDefault();
        speak(card.word.original_text, 'en-US');
        return;
      }
      // E работает всегда — и до, и после ответа
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        if (!showExplain && !isLoadingExplain) handleExplain();
        return;
      }
      if (revealed) {
        if (!autoAdvance && answerSubmitted && (e.key === 'Enter' || e.code === 'NumpadEnter')) {
          e.preventDefault();
          onManualAdvance?.();
          return;
        }
        if (autoAdvance && showExplain && (e.key === 'Enter' || e.code === 'NumpadEnter')) {
          e.preventDefault();
          handleManualAdvanceWithAnswer();
          return;
        }
        return;
      }
      const keyToIdx: Record<string, number> = { '1': 0, 'Numpad1': 0, '2': 1, 'Numpad2': 1, '3': 2, 'Numpad3': 2, '4': 3, 'Numpad4': 3 };
      const idx = keyToIdx[e.key] ?? keyToIdx[e.code];
      if (idx !== undefined && options[idx] !== undefined) {
        e.preventDefault();
        handleSelect(options[idx]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop]);

  // Закрываем попапы при смене карточки
  useEffect(() => { setOpenPopup(null); }, [card.word.id]);

  // Закрываем попапы по клику вне карточки
  useEffect(() => {
    if (!openPopup) return;
    const close = () => setOpenPopup(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openPopup]);

  const posInfo = card.word.part_of_speech ? POS_INFO[card.word.part_of_speech] : null;
  const cefrInfo = card.word.cefr_level ? CEFR_INFO[card.word.cefr_level] : null;
  const posColor = card.word.part_of_speech ? (POS_COLORS[card.word.part_of_speech] || 'var(--theme-border)') : 'var(--theme-border)';

  const example = card.word.examples?.[0];
  const isFooterOpen = (!autoAdvance && answerSubmitted) || (autoAdvance && revealed && showExplain);

  return (
    <div style={{ padding: '12px 16px', paddingBottom: isFooterOpen ? 120 : 20 }}>
      {/* Direction indicator + progress dots */}
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
          {card.direction === 'original_to_translation' ? 'EN → RU' : 'RU → EN'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {Array.from({ length: requiredReps }).map((_, i) => {
              const done = i < card.word.mc_en_ru_progress;
              const current = !revealed && !done && i === card.word.mc_en_ru_progress && isEnDirection;
              return (
                <svg key={`enru-${i}`} width="10" height="10" viewBox="0 0 10 10">
                  <circle cx="5" cy="5" r="4"
                    fill={done ? 'var(--theme-success)' : 'none'}
                    stroke={done ? 'var(--theme-success)' : current ? 'var(--theme-primary)' : 'var(--theme-border)'}
                    strokeWidth={current ? 2 : 1.5}
                  />
                </svg>
              );
            })}
            <span style={{ fontSize: 9, color: 'var(--theme-text-muted)', marginLeft: 2 }}>EN→RU</span>
          </div>
          <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {Array.from({ length: requiredReps }).map((_, i) => {
              const done = i < card.word.mc_ru_en_progress;
              const current = !revealed && !done && i === card.word.mc_ru_en_progress && !isEnDirection;
              return (
                <svg key={`ruen-${i}`} width="10" height="10" viewBox="0 0 10 10">
                  <circle cx="5" cy="5" r="4"
                    fill={done ? 'var(--theme-success)' : 'none'}
                    stroke={done ? 'var(--theme-success)' : current ? 'var(--theme-primary)' : 'var(--theme-border)'}
                    strokeWidth={current ? 2 : 1.5}
                  />
                </svg>
              );
            })}
            <span style={{ fontSize: 9, color: 'var(--theme-text-muted)', marginLeft: 2 }}>RU→EN</span>
          </div>
        </div>
      </div>

      {/* Difficulty indicator */}
      {card.hint?.difficulty_indicator && (
        <div style={{
          marginBottom: 12,
          padding: '6px 12px',
          background: `${difficultyColor}15`,
          border: `1px solid ${difficultyColor}`,
          borderRadius: 8,
          fontSize: 12,
          color: difficultyColor || 'var(--theme-text)',
        }}>
          {card.hint.difficulty_indicator}
        </div>
      )}

      {/* Question word — outer wrapper для попапов (overflow: visible) */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        {/* Card — overflow: hidden для конфетти */}
        <div
          style={{
            display: 'flex',
            background: 'var(--theme-bg-elevated)',
            borderRadius: 12,
            minHeight: 110,
            border: isMastered ? '2px solid var(--theme-primary)' : '0.5px solid var(--theme-border)',
            overflow: 'hidden',
            transition: 'border-color 0.3s ease',
          }}
        >
          {/* Цветная полоска слева (по части речи) */}
          <div style={{
            width: 4,
            background: posColor,
            flexShrink: 0,
            borderRadius: '12px 0 0 12px',
          }} />

          {/* Контентная область */}
          <div
            onClick={handleTtsClick}
            style={{
              flex: 1,
              padding: '14px 12px',
              position: 'relative',
              cursor: (isEnDirection || revealed) ? 'pointer' : 'default',
            }}
          >
            {/* Конфетти */}
            {isMastered && CONFETTI.map((c, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: `${c.left}%`,
                  width: c.shape === 'circle' ? 8 : 7,
                  height: c.shape === 'circle' ? 8 : 7,
                  borderRadius: c.shape === 'circle' ? '50%' : 2,
                  background: c.color,
                  animation: `confetti-rise ${c.dur}s ${c.delay}s ease-out both`,
                  pointerEvents: 'none',
                }}
              />
            ))}

            {/* Теги POS/CEFR + admin ✏️. Строка рендерится всегда, чтобы кнопка
                могла появиться даже у слов без POS/CEFR/dict_word_id. */}
            {true && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap', minHeight: 22 }}>
                {posInfo && (
                  <span
                    onClick={(e) => { e.stopPropagation(); setOpenPopup(p => p === 'pos' ? null : 'pos'); }}
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: `${posColor}18`,
                      color: posColor,
                      border: `1px solid ${posColor}40`,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    {card.word.part_of_speech} — {posInfo.ru}
                  </span>
                )}
                {cefrInfo && (
                  <span
                    ref={cefrBadgeRef}
                    onClick={(e) => { e.stopPropagation(); setOpenPopup(p => p === 'cefr' ? null : 'cefr'); }}
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: 'rgba(239,159,39,0.12)',
                      color: '#D4870A',
                      border: '1px solid rgba(239,159,39,0.3)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    {card.word.cefr_level}
                  </span>
                )}
                {card.word.plural_form && (isEnDirection || revealed) && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 7px',
                      borderRadius: 4,
                      background: 'rgba(56, 189, 248, 0.12)',
                      color: '#0284c7',
                      border: '1px solid rgba(56, 189, 248, 0.3)',
                      userSelect: 'none',
                    }}
                  >
                    мн. ч.: {card.word.plural_form}
                  </span>
                )}
                <AdminEditButton
                  dictionaryWordId={(card.word as any).dictionary_word_id}
                  userWordId={card.word.id}
                  onSaved={(updated) => onWordEdited?.(updated)}
                  inline
                />
              </div>
            )}

            {/* Слово с ударением */}
            <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--theme-text)', lineHeight: 1.2 }}>
              {isEnDirection && card.word.transcription
                ? addStressMark(question, card.word.transcription)
                : question
              }
            </div>

            {/* Произношение — только EN→RU и если есть данные */}
            {isEnDirection && (card.word.transcription || card.word.russian_phonetic) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 13 }}>
                {card.word.transcription && (
                  <span style={{ color: 'var(--theme-text-muted)' }}>{card.word.transcription}</span>
                )}
                {card.word.russian_phonetic && (
                  <span style={{ color: 'var(--theme-text-secondary, #666)' }}>
                    {addPhoneticStress(card.word.russian_phonetic, card.word.transcription || '')}
                  </span>
                )}
              </div>
            )}

            {/* Текст "Изучено" убран — показывается как тост вверху страницы */}
          </div>

          {/* Правая панель 78px — цветные кнопки, SVG-иконки */}
          <div style={{
            width: 78,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '0.5px solid var(--theme-border)',
            flexShrink: 0,
          }}>
            {/* TTS */}
            <button
              onClick={(e) => { e.stopPropagation(); handleTtsClick(); }}
              disabled={!isEnDirection && !revealed}
              style={{
                flex: 1,
                background: 'rgba(55, 138, 221, 0.08)',
                border: 'none',
                borderBottom: '0.5px solid var(--theme-border)',
                borderRadius: '0 12px 0 0',
                cursor: (!isEnDirection && !revealed) ? 'default' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                opacity: (!isEnDirection && !revealed) ? 0.3 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#378ADD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
              </svg>
            </button>

            {/* Explain */}
            <button
              onClick={(e) => { e.stopPropagation(); handleExplain(); }}
              disabled={isLoadingExplain}
              style={{
                flex: 1,
                background: showExplain ? 'rgba(239, 159, 39, 0.15)' : 'rgba(239, 159, 39, 0.08)',
                border: 'none',
                borderRadius: '0 0 12px 0',
                cursor: (showExplain || isLoadingExplain) ? 'default' : 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                opacity: isLoadingExplain ? 0.5 : 1,
                transition: 'all 0.2s',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4870A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6"/>
                <path d="M10 22h4"/>
                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Попап POS — вне overflow:hidden */}
        {openPopup === 'pos' && posInfo && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: 48, left: 16, width: 260, zIndex: 10,
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 10, padding: '12px 14px', fontSize: 13,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ position: 'absolute', top: -6, left: 16, width: 12, height: 12,
              background: 'var(--theme-bg-elevated)', borderLeft: '1px solid var(--theme-border)',
              borderTop: '1px solid var(--theme-border)', transform: 'rotate(45deg)' }} />
            <div style={{ fontWeight: 600, color: posColor, marginBottom: 4 }}>
              {posInfo.en.charAt(0).toUpperCase() + posInfo.en.slice(1)} ({posInfo.ru})
            </div>
            <div style={{ color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{posInfo.description}</div>
            <div style={{ color: 'var(--theme-text-muted)', fontStyle: 'italic' }}>{posInfo.examples}</div>
          </div>
        )}

        {/* Попап CEFR — вне overflow:hidden */}
        {openPopup === 'cefr' && cefrInfo && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: 48, left: 16, width: 260, zIndex: 10,
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 10, padding: '12px 14px', fontSize: 13,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ position: 'absolute', top: -6, left: cefrBadgeRef.current
                ? cefrBadgeRef.current.offsetLeft + cefrBadgeRef.current.offsetWidth / 2 - 18
                : 16, width: 12, height: 12,
              background: 'var(--theme-bg-elevated)', borderLeft: '1px solid var(--theme-border)',
              borderTop: '1px solid var(--theme-border)', transform: 'rotate(45deg)' }} />
            <div style={{ fontWeight: 600, color: '#854F0B', marginBottom: 4 }}>
              {card.word.cefr_level} — {cefrInfo.title}
            </div>
            <div style={{ color: 'var(--theme-text-secondary)' }}>{cefrInfo.description}</div>
          </div>
        )}
      </div>

      {/* Adaptive hint with AI */}
      {card.hint && (
        <AiHintBlock
          card={card}
          difficultyColor={difficultyColor}
        />
      )}

      {/* Контейнер примера использования */}
      {(() => {
        const rawExamples = (card as any).context_examples?.length
          ? (card as any).context_examples
          : card.context_sentence
            ? [card.context_sentence]
            : (card.word.examples || []).map((e: any) => ({
                en: e.example_sentence,
                en_blanked: e.example_sentence,
                ru: e.example_translation,
                ru_blanked: e.example_translation,
              }));
        if (!rawExamples || rawExamples.length === 0) return null;
        return (
          <ExampleBlock
            examples={rawExamples}
            wordOriginal={card.word.original_text}
            wordTranslation={card.word.main_translation_text}
            direction={card.direction}
            isAnswered={revealed}
          />
        );
      })()}

      {/* AI объяснение от 2EnBota — под словом, над вариантами */}
      {showExplain && (
        <div style={{
          marginBottom: 12,
          padding: '14px 16px',
          background: 'var(--theme-bg-elevated)',
          borderRadius: 14,
          borderLeft: '4px solid',
          borderImage: 'linear-gradient(to bottom, #2196F3, #8B5CF6, #EC4899) 1',
          fontSize: 13,
          maxHeight: 400,
          overflowY: 'auto',
        }}>
          {isLoadingExplain ? (
            <div style={{ color: 'var(--theme-text-muted)', textAlign: 'center', padding: '16px 0' }}>
              🦊🦝 Готовим объяснение...
            </div>
          ) : explanation ? (
            <ExplanationView data={explanation} />
          ) : (
            <div style={{ color: 'var(--theme-text-muted)' }}>🦊🦝 Не удалось загрузить</div>
          )}
        </div>
      )}

      {/* Options */}
      <div data-tour-target="mc-card" style={{ display: 'grid', gap: 7 }}>
        {(card.options || []).map((option, idx) => {
          const isSelected = selected === option;
          const isCorrectOption = option === correctAnswer;

          let bg = 'var(--theme-bg-card)';
          let border = 'var(--theme-border)';
          let color = 'var(--theme-text)';

          if (revealed) {
            if (isCorrectOption) {
              bg = 'rgba(0, 230, 118, 0.1)';
              border = 'var(--theme-success)';
              color = 'var(--theme-success)';
            } else if (isSelected && !isCorrectOption) {
              bg = 'rgba(255, 82, 82, 0.1)';
              border = 'var(--theme-error)';
              color = 'var(--theme-error)';
            }
          }

          return (
            <button
              key={idx}
              onClick={() => handleSelect(option)}
              disabled={revealed}
              style={{
                padding: '12px 14px',
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 10,
                color,
                fontSize: 14,
                fontWeight: 500,
                cursor: revealed ? 'default' : 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
                minHeight: '42px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              {isDesktop && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  background: 'var(--theme-border)',
                  border: '1px solid var(--theme-border)',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--theme-text-muted)',
                  flexShrink: 0,
                }}>
                  {idx + 1}
                </span>
              )}
              {option}
            </button>
          );
        })}
      </div>

      {/* Зафиксированная нижняя панель (Fixed Footer) — ВСЕГДА видна внизу экрана без скролла */}
      {isFooterOpen && (() => {
        // Все переводы слова (основной + альтернативные)
        const allTranslations = Array.from(new Set([card.word.main_translation_text, ...card.word.translations.map(t => t.translation_text)]));
        // Исключаем правильный ответ и слово-вопрос (чтобы не дублировать)
        const norm = (s: string) => s.trim().toLowerCase();
        const excludeSet = new Set([norm(correctAnswer), norm(question)]);
        const extraTranslations = allTranslations.filter(t => !excludeSet.has(norm(t)));

        return (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'var(--theme-bg-overlay, rgba(15, 15, 26, 0.96))',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderTop: '1px solid var(--theme-border)',
            padding: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
            zIndex: 100,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 480,
            margin: '0 auto',
            animation: 'fadeIn 0.2s ease',
          }}>
            {/* Доп. переводы — прямо в фиксированной панели над кнопкой (без обрезки) */}
            {extraTranslations.length > 0 && (
              <div style={{
                fontSize: 12.5,
                color: 'var(--theme-text-secondary)',
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                lineHeight: 1.35,
                flexWrap: 'wrap',
              }}>
                <span style={{ fontWeight: 700, color: selected === correctAnswer ? 'var(--theme-success)' : 'var(--theme-error)' }}>
                  {selected === correctAnswer ? '✓' : '✗'}
                </span>
                <span style={{ opacity: 0.7, flexShrink: 0 }}>Ещё:</span>
                <span style={{ fontWeight: 600, color: 'var(--theme-primary)', wordBreak: 'break-word' }}>
                  {extraTranslations.join(', ')}
                </span>
              </div>
            )}

            <button
              onClick={autoAdvance && showExplain ? handleManualAdvanceWithAnswer : onManualAdvance}
              style={{
                width: '100%',
                padding: '13px',
                background: 'var(--theme-primary)',
                color: 'var(--theme-primary-text)',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              }}
            >
              Далее →
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// =====================
// Audition Card (слушай, выбирай перевод)
// =====================

function AuditionCard({ card, onAnswer, isDesktop = false }: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean) => void;
  isDesktop?: boolean;
}) {
  const { speak, stop } = useTTS();
  const { notify, selection } = useHapticFeedback();
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  const isRuEn = card.direction === 'translation_to_original';
  const promptWord = card.prompt_text || card.word.original_text;
  const correctAnswer = card.correct_answer ?? card.word.main_translation_text;
  const ttsLang = isRuEn ? 'ru-RU' : 'en-US';

  useEffect(() => {
    speak(promptWord, ttsLang);
    return () => { stop(); };
  }, [promptWord, ttsLang, speak, stop]);

  const handleSelect = (option: string) => {
    if (revealed) return;
    selection();
    setSelected(option);
    setRevealed(true);
    const correct = option === correctAnswer;
    setIsCorrect(correct);
    notify(correct ? 'success' : 'error');
    if (correct) {
      playSuccessSound();
    } else {
      playErrorSound();
    }
    trackEvent('audition_answer_selected', {
      word_id: card.word.id,
      word: promptWord,
      correct_answer: correctAnswer,
      selected: option,
      is_correct: correct,
      options: card.options,
    });
    onAnswer(correct);
  };

  // Ref для актуальных значений в keyboard handler
  const kbRef = useRef({ revealed, options: card.options || [], handleSelect, promptWord, speak, ttsLang });
  kbRef.current = { revealed, options: card.options || [], handleSelect, promptWord, speak, ttsLang };

  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      const { revealed, options, handleSelect, promptWord, speak, ttsLang } = kbRef.current;
      if (revealed) return; // Enter обрабатывается глобально в StudyPage
      // Shift+2 — повторить аудио
      if (e.shiftKey && (e.key === '2' || e.code === 'Digit2' || e.code === 'Numpad2')) {
        e.preventDefault();
        speak(promptWord, ttsLang);
        return;
      }
      const keyToIdx: Record<string, number> = { '1': 0, 'Numpad1': 0, '2': 1, 'Numpad2': 1, '3': 2, 'Numpad3': 2, '4': 3, 'Numpad4': 3 };
      const idx = keyToIdx[e.key] ?? keyToIdx[e.code];
      if (idx !== undefined && options[idx] !== undefined) {
        e.preventDefault();
        handleSelect(options[idx]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop]);

  const allTranslations = Array.from(new Set([
    card.word.main_translation_text,
    ...(card.word.translations?.map(t => t.translation_text) || []),
  ].filter(Boolean)));

  const contextSentence = card.context_sentence;

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
        Слушай и Выбирай
      </div>

      {/* Play button */}
      <div style={{
        background: 'var(--theme-bg-elevated)',
        borderRadius: 16,
        padding: '32px 20px',
        marginBottom: 16,
        textAlign: 'center',
      }}>
        <button
          onClick={() => speak(promptWord, ttsLang)}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 64,
            cursor: 'pointer',
            display: 'block',
            margin: '0 auto 8px',
          }}
        >
          🔊
        </button>
        <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
          Нажми для повтора
        </div>
      </div>

      {/* Options */}
      {!revealed && (
        <div style={{ display: 'grid', gap: 8 }}>
          {(card.options || []).map((option, idx) => (
            <button key={idx} onClick={() => handleSelect(option)} style={{
              padding: '14px 16px', background: 'var(--theme-bg-card)', border: '1px solid var(--theme-border)',
              borderRadius: 10, color: 'var(--theme-text)', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', textAlign: 'left', minHeight: '44px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              {isDesktop && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: 5,
                  background: 'var(--theme-border)', border: '1px solid var(--theme-border)',
                  fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', flexShrink: 0,
                }}>
                  {idx + 1}
                </span>
              )}
              {option}
            </button>
          ))}
        </div>
      )}

      {/* Feedback */}
      {revealed && isCorrect !== null && (
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
          {/* Result banner */}
          <div style={{
            padding: '14px 16px', marginBottom: 16, borderRadius: 12, textAlign: 'center',
            background: isCorrect ? 'rgba(0,230,118,0.08)' : 'rgba(255,82,82,0.08)',
            border: `1px solid ${isCorrect ? 'var(--theme-success)' : 'var(--theme-error)'}`,
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: isCorrect ? 'var(--theme-success)' : 'var(--theme-error)' }}>
              {isCorrect ? '✓ Правильно!' : '✗ Неверно'}
            </div>
          </div>

          {/* Word reveal */}
          <div style={{
            background: 'var(--theme-bg-elevated)', borderRadius: 16, padding: '24px 20px',
            marginBottom: 16, textAlign: 'center',
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 4 }}>
              {isRuEn ? card.word.original_text : promptWord}
            </div>
            {(card.word.transcription || card.word.part_of_speech || card.word.plural_form) && (
              <div style={{ fontSize: 14, color: 'var(--theme-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                {card.word.transcription && <span>{card.word.transcription}</span>}
                {card.word.part_of_speech && (
                  <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: POS_COLORS[card.word.part_of_speech] || 'var(--theme-text-muted)' }}>
                    {POS_INFO[card.word.part_of_speech]?.en || card.word.part_of_speech}
                  </span>
                )}
                {card.word.plural_form && (
                  <span style={{ fontSize: 11, color: '#0284c7', background: 'rgba(56, 189, 248, 0.12)', padding: '2px 7px', borderRadius: 4, border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                    мн. ч.: {card.word.plural_form}
                  </span>
                )}
              </div>
            )}
            <div style={{ fontSize: 15, color: 'var(--theme-text-secondary)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--theme-text)' }}>
                {isRuEn ? card.word.main_translation_text : allTranslations.join(', ')}
              </strong>
              {!isRuEn && card.word.translations && card.word.translations.length > 0 && (
                <span>
                  {', '}
                  {card.word.translations.map((t, i) => (
                    <span key={i} style={{ color: 'var(--theme-text)' }}>
                      {i > 0 && ', '}
                      {t.translation_text}
                    </span>
                  ))}
                </span>
              )}
              {isRuEn && card.word.translations && card.word.translations.length > 0 && (
                <span>
                  {', '}
                  {card.word.translations.map((t, i) => (
                    <span key={i} style={{ color: 'var(--theme-text)' }}>
                      {i > 0 && ', '}
                      {t.translation_text}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </div>

          {/* Examples */}
          {(() => {
            const rawExamples = (card as any).context_examples?.length
              ? (card as any).context_examples
              : card.context_sentence
                ? [card.context_sentence]
                : (card.word.examples || []).map((e: any) => ({
                    en: e.example_sentence,
                    en_blanked: e.example_sentence,
                    ru: e.example_translation,
                    ru_blanked: e.example_translation,
                  }));
            if (!rawExamples || rawExamples.length === 0) return null;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, textAlign: 'center' }}>
                  Примеры
                </div>
                {rawExamples.map((ex: any, i: number) => (
                  <div key={i} style={{
                    background: 'var(--theme-bg-card)', borderRadius: 12, padding: '14px 16px',
                    marginBottom: 8, border: '1px solid var(--theme-border)',
                  }}>
                    <div style={{ fontSize: 14, color: 'var(--theme-text)', fontStyle: 'italic', lineHeight: 1.55, marginBottom: 6 }}>
                      {renderHighlightedSentence(ex.en || ex.example_sentence, card.word.original_text)}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>
                      → {ex.ru || ex.example_translation}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// =====================
// Dictation Card (слушай, пиши слово)
// =====================

function DictationCard({ card, onAnswer, isDesktop = false, pendingAiFeedback }: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean, userInput: string, isRetry?: boolean) => void;
  isDesktop?: boolean;
  pendingAiFeedback?: AiFeedback | null;
}) {
  const { speak, stop, speaking } = useTTS();
  const { notify, impact } = useHapticFeedback();
  const [input, setInput] = useState('');
  const [lastUserInput, setLastUserInput] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'correct' | 'feedback' | 'typo'>('idle');
  const [typoHint, setTypoHint] = useState<string | null>(null);
  const hasSpoken = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRuEn = card.direction === 'translation_to_original';
  const promptWord = card.prompt_text || card.word.original_text;
  const expectedAnswer = card.expected_answer || card.word.original_text;
  const allTranslations = isRuEn
    ? [expectedAnswer]
    : [
        card.word.main_translation_text,
        ...card.word.translations.map(t => t.translation_text),
      ].filter(Boolean);
  const ttsLang = isRuEn ? 'ru-RU' : 'en-US';

  const checkTranslation = (val: string) => {
    if (allTranslations.length === 0) return false;
    return checkAnswer(val, allTranslations[0], allTranslations.slice(1));
  };

  // Levenshtein distance для опечаток
  const levenshtein = (a: string, b: string): number => {
    const m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
    return dp[m][n];
  };

  const findTypoMatch = (val: string) => {
    const norm = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ');
    const nv = norm(val);
    if (nv.length < 2) return null;
    for (const t of allTranslations) {
      const tv = norm(t);
      const dist = levenshtein(nv, tv);
      if (dist >= 1 && dist <= 2 && nv.length >= 3) {
        return tv;
      }
    }
    return null;
  };

  // Сброс при смене слова
  useEffect(() => {
    setInput('');
    setLastUserInput('');
    setStatus('idle');
    setTypoHint(null);
    hasSpoken.current = false;
  }, [card.word.id]);

  // TTS autoplay
  useEffect(() => {
    if (!hasSpoken.current) {
      hasSpoken.current = true;
      speak(promptWord, ttsLang);
    }
    return () => { stop(); };
  }, [promptWord, ttsLang, speak, stop]);

  // Автофокус на поле ввода после неправильного ответа (feedback / typo)
  useEffect(() => {
    if (status === 'feedback' || status === 'typo') {
      inputRef.current?.focus();
    }
  }, [status]);

  // Хоткей Shift+2 — повторить аудио
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      if (e.shiftKey && (e.key === '2' || e.code === 'Digit2' || e.code === 'Numpad2')) {
        e.preventDefault();
        speak(promptWord, ttsLang);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop, speak, promptWord, ttsLang]);

  // Когда пришла AI-подсказка с сервера — переключаем из checking в feedback
  useEffect(() => {
    if (pendingAiFeedback && status === 'checking') {
      setStatus('feedback');
      setInput('');
      setTypoHint(null);
    }
  }, [pendingAiFeedback, status]);

  const handleSubmit = () => {
    if (status === 'checking') return;

    // Пустой ответ — локальный фидбек, без вызова AI
    if (!input.trim()) {
      notify('warning');
      playErrorSound();
      setStatus('feedback');
      setInput('');
      setTypoHint(null);
      return;
    }

    const isCorrect = checkTranslation(input);
    const isRetry = status === 'feedback' || status === 'typo';
    setLastUserInput(input.trim());

    if (isCorrect) {
      setStatus('correct');
      setTypoHint(null);
      if (isRetry) {
        impact('light');
      } else {
        notify('success');
      }
      playSuccessSound();
      onAnswer(true, input, isRetry);
      return;
    }

    // Опечатка (1-2 буквы разницы) — локальный фидбек без вызова AI
    const typoMatch = findTypoMatch(input);
    if (!isRetry && typoMatch) {
      setTypoHint(typoMatch);
      setStatus('typo');
      setInput('');
      notify('warning');
      playErrorSound();
      return;
    }

    if (!isRetry) {
      // Первая ошибка с текстом — запрашиваем AI-подсказку с сервера
      setStatus('checking');
      setTypoHint(null);
      notify('error');
      playErrorSound();
      onAnswer(false, input);
      return;
    }

    // Уже был feedback и снова неверно — остаёмся на карточке, поле очищаем
    setInput('');
    setTypoHint(null);
    notify('error');
    playErrorSound();
    onAnswer(false, input, true);
  };

  // Альтернативные переводы (исключая тот, что пользователь уже видел как основной)
  const seenNorm = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ');
  const otherTranslations = allTranslations.filter(t => seenNorm(t) !== seenNorm(card.word.main_translation_text || ''));

  const showInput = status !== 'correct';
  const inputDisabled = status === 'checking';
  const anchorRef = useKeyboardScroll(showInput);

  return (
    <div style={{ padding: '20px 16px' }}>
      <style>{`
        @keyframes pulse-speaker {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.12); opacity: 0.85; }
        }
      `}</style>
      <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' }}>
        Слово на слух
      </div>

      <div style={{ background: 'var(--theme-bg-elevated)', borderRadius: 16, padding: '24px 20px', marginBottom: 20, textAlign: 'center' }}>
        <button
          onClick={() => speak(promptWord, ttsLang)}
          style={{
            background: 'none', border: 'none', fontSize: 56, cursor: 'pointer',
            display: 'block', margin: '0 auto 8px',
            animation: speaking ? 'pulse-speaker 1s ease-in-out infinite' : 'none',
          }}
        >
          {speaking ? '🔊' : '🔈'}
        </button>
        <div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>
          {speaking ? 'Слушаем...' : 'Нажмите, чтобы повторить'}
        </div>
      </div>

      {showInput && (
        <div style={{ marginBottom: 12 }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => !inputDisabled && setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !inputDisabled && handleSubmit()}
            placeholder={status === 'feedback' || status === 'typo' ? 'Попробуй ещё раз...' : isRuEn ? 'Введите перевод на английском...' : 'Введите перевод по-русски...'}
            autoFocus
            disabled={inputDisabled}
            style={{
              width: '100%', padding: '14px',
              background: status === 'feedback' || status === 'typo' ? 'rgba(255,82,82,0.08)' : 'var(--theme-bg-elevated)',
              border: `1px solid ${status === 'feedback' || status === 'typo' ? 'var(--theme-error)' : 'var(--theme-border)'}`,
              borderRadius: 10, color: 'var(--theme-text)',
              fontSize: 18, outline: 'none', textAlign: 'center', boxSizing: 'border-box',
              opacity: inputDisabled ? 0.6 : 1,
            }}
          />
        </div>
      )}

      {/* Smart Feedback Card */}
      {(status === 'typo' || status === 'feedback') && (
        <SmartInputFeedback
          userInput={lastUserInput}
          expectedAnswer={isRuEn ? expectedAnswer : card.word.main_translation_text}
          otherTranslations={isRuEn ? [] : otherTranslations}
          feedbackType={
            status === 'typo'
              ? 'typo'
              : pendingAiFeedback?.error_type === 'synonym'
                ? 'synonym'
                : pendingAiFeedback?.error_type === 'layout'
                  ? 'layout'
                  : pendingAiFeedback?.error_type === 'grammar'
                    ? 'grammar'
                    : 'wrong'
          }
          aiHint={pendingAiFeedback?.hint}
          typoCorrection={typoHint || (isRuEn ? expectedAnswer : card.word.main_translation_text)}
        />
      )}

      {/* Правильный ответ + примеры */}
      {status === 'correct' && (
        <div>
          <div style={{
            padding: 16, background: 'rgba(0, 230, 118, 0.12)', border: '1px solid var(--theme-success)',
            borderRadius: 12, textAlign: 'center', marginBottom: 12,
            animation: 'fadeIn 0.3s ease',
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--theme-success)' }}>
              Молодец! ✓
            </div>
            {otherTranslations.length > 0 && (
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginTop: 6 }}>
                А ещё <strong style={{ color: 'var(--theme-text)' }}>{expectedAnswer}</strong> можно перевести как{' '}
                {otherTranslations.map((t, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    <strong style={{ color: 'var(--theme-text)' }}>{t}</strong>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Examples */}
          {(() => {
            const rawExamples = (card as any).context_examples?.length
              ? (card as any).context_examples
              : card.context_sentence
                ? [card.context_sentence]
                : (card.word.examples || []).map((e: any) => ({
                    en: e.example_sentence,
                    en_blanked: e.example_sentence,
                    ru: e.example_translation,
                    ru_blanked: e.example_translation,
                  }));
            if (!rawExamples || rawExamples.length === 0) return null;
            return (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, textAlign: 'center' }}>
                  Примеры
                </div>
                {rawExamples.map((ex: any, i: number) => (
                  <div key={i} style={{
                    padding: '12px 14px', marginBottom: 8,
                    background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)',
                    borderRadius: 10, fontSize: 14,
                  }}>
                    <div style={{ color: 'var(--theme-text)', marginBottom: 4, fontStyle: 'italic' }}>
                      {renderHighlightedSentence(ex.en || ex.example_sentence, expectedAnswer)}
                    </div>
                    <div style={{ color: 'var(--theme-text-secondary)', fontSize: 13 }}>
                      → {ex.ru || ex.example_translation}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {showInput && (
        <button
          onClick={handleSubmit}
          disabled={inputDisabled}
          style={{
            width: '100%', padding: '14px',
            background: inputDisabled ? 'var(--theme-border)' : 'var(--theme-primary)',
            color: inputDisabled ? 'var(--theme-text)' : 'var(--theme-primary-text)', border: 'none', borderRadius: 10,
            fontSize: 16, fontWeight: 600,
            cursor: inputDisabled ? 'not-allowed' : 'pointer', minHeight: '44px',
          }}
        >
          {status === 'checking' ? 'Проверяю...' : 'Проверить'}
        </button>
      )}
      <div ref={anchorRef} style={{ height: 20 }} />
    </div>
  );
}

// =====================
// Course Input Card (Ввод с клавиатуры в курсах)
// =====================

function CourseInputCard({
  card,
  onAnswer,
  isDesktop = false,
  pendingAiFeedback,
  autoAdvance = true,
  onAdvance,
  isErrorRound = false,
}: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean, userInput: string, isRetry?: boolean, spareLife?: boolean, hasMistake?: boolean) => Promise<any> | void;
  isDesktop?: boolean;
  pendingAiFeedback?: AiFeedback | null;
  autoAdvance?: boolean;
  onAdvance?: () => void;
  isErrorRound?: boolean;
}) {
  const { speak, speaking } = useTTS();
  const { notify, impact } = useHapticFeedback();
  const [input, setInput] = useState('');
  const [lastUserInput, setLastUserInput] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'checking' | 'correct' | 'feedback' | 'typo'>('idle');
  const [typoHint, setTypoHint] = useState<string | null>(null);
  const [hasMistake, setHasMistake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const promptWord = card.word.main_translation_text || '';
  const expectedAnswer = card.word.original_text || '';
  const allTranslations = [
    expectedAnswer,
    ...card.word.translations.map(t => t.translation_text),
  ].filter(Boolean);

  const checkTranslation = (val: string) => {
    return checkAnswer(val, expectedAnswer, allTranslations.slice(1));
  };

  const levenshtein = (a: string, b: string): number => {
    const m = a.length, n = b.length;
    if (!m) return n;
    if (!n) return m;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        dp[i][j] = a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
    return dp[m][n];
  };

  const findTypoMatch = (val: string) => {
    const norm = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ');
    const nv = norm(val);
    if (nv.length < 2) return null;
    const tv = norm(expectedAnswer);
    const dist = levenshtein(nv, tv);
    if (dist >= 1 && dist <= 2 && nv.length >= 3) {
      return tv;
    }
    return null;
  };

  useEffect(() => {
    setInput('');
    setLastUserInput(null);
    setStatus('idle');
    setTypoHint(null);
    setHasMistake(false);
    const timer = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, [card.word.id]);

  useEffect(() => {
    if (pendingAiFeedback) {
      setStatus('feedback');
      setTypoHint(null);
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [pendingAiFeedback]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (status === 'checking' || status === 'correct') return;

    if (!input.trim()) {
      notify('warning');
      playErrorSound();
      return;
    }

    const isCorrect = checkTranslation(input);

    if (isCorrect) {
      setStatus('correct');
      setTypoHint(null);
      if (hasMistake) impact('medium');
      else notify('success');
      playSuccessSound();
      speak(expectedAnswer, 'en-US');
      await onAnswer(true, input, false, false, hasMistake);
      if (autoAdvance && onAdvance) {
        setTimeout(() => {
          onAdvance();
        }, 1200);
      }
      return;
    }

    const currentVal = input.trim();
    setLastUserInput(currentVal);

    // Ошибка: фиксируем, что карточка решена с ошибкой
    const isFirstMistake = !hasMistake;
    setHasMistake(true);

    const typoMatch = findTypoMatch(currentVal);
    if (typoMatch) {
      setTypoHint(typoMatch);
      setStatus('typo');
      notify('warning');
      playErrorSound();
      setTimeout(() => inputRef.current?.select(), 50);
      try {
        await onAnswer(false, currentVal, !isFirstMistake, false, true);
      } catch {
        // ignore
      }
      return;
    }

    setStatus('checking');
    setTypoHint(null);
    notify('error');
    playErrorSound();
    try {
      await onAnswer(false, currentVal, !isFirstMistake, false, true);
    } finally {
      setStatus('feedback');
      setTimeout(() => inputRef.current?.select(), 50);
    }
  };

  const showInput = status !== 'correct';
  const inputDisabled = status === 'checking';
  useKeyboardScroll(showInput);

  return (
    <div style={{ padding: '20px 16px', maxWidth: 460, margin: '0 auto' }}>
      <div style={{
        marginBottom: 14,
        textAlign: 'center',
      }}>
        {isErrorRound ? (
          <span style={{
            fontSize: 12,
            fontWeight: 800,
            color: '#F59E0B',
            textTransform: 'uppercase',
            letterSpacing: 1,
            background: 'rgba(245, 158, 11, 0.12)',
            padding: '5px 12px',
            borderRadius: 16,
            border: '1px solid rgba(245, 158, 11, 0.35)',
            display: 'inline-block',
          }}>
            🔁 Работа над ошибками: введите из памяти
          </span>
        ) : (
          <span style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--theme-primary)',
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}>
            ✍️ Шаг 2: Ввод с клавиатуры
          </span>
        )}
      </div>

      <div style={{
        background: 'var(--theme-bg-elevated)',
        borderRadius: 18,
        padding: '24px 20px',
        marginBottom: 20,
        textAlign: 'center',
        border: '1px solid var(--theme-border)',
        boxShadow: 'var(--theme-shadow)',
      }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 6 }}>
          {promptWord}
        </div>

        {card.word.transcription && (
          <div style={{ fontSize: 13, color: 'var(--theme-text-muted)', marginBottom: 10 }}>
            [{card.word.transcription}]
          </div>
        )}

        {/* Озвучивание слова как подсказка */}
        <button
          type="button"
          onClick={() => speak(expectedAnswer, 'en-US')}
          style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            borderRadius: 20,
            padding: '6px 14px',
            color: '#f59e0b',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span>{speaking ? '🔊' : '💡'}</span>
          <span>{speaking ? 'Слушаем...' : 'Подсказка: послушать слово'}</span>
        </button>
      </div>

      {showInput && (
        <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
          <div style={{ position: 'relative' }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => !inputDisabled && setInput(e.target.value)}
              placeholder="Напечатайте по-английски..."
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              disabled={inputDisabled}
              style={{
                width: '100%',
                padding: '16px 20px',
                background: status === 'feedback' || status === 'typo' ? 'rgba(239, 68, 68, 0.08)' : 'var(--theme-bg-elevated)',
                border: `2px solid ${status === 'feedback' || status === 'typo' ? 'var(--theme-error)' : 'var(--theme-border)'}`,
                borderRadius: 14,
                color: 'var(--theme-text)',
                fontSize: 18,
                fontWeight: 600,
                outline: 'none',
                textAlign: 'center',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={inputDisabled || !input.trim()}
            style={{
              width: '100%',
              marginTop: 12,
              padding: '14px',
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text)',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: inputDisabled || !input.trim() ? 'not-allowed' : 'pointer',
              opacity: inputDisabled || !input.trim() ? 0.5 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {status === 'checking' ? 'Проверяем...' : 'Проверить →'}
          </button>
        </form>
      )}

      {/* Smart Feedback Card */}
      {(status === 'typo' || status === 'feedback') && (
        <SmartInputFeedback
          userInput={lastUserInput || ''}
          expectedAnswer={expectedAnswer}
          feedbackType={
            status === 'typo'
              ? 'typo'
              : pendingAiFeedback?.error_type === 'synonym'
                ? 'synonym'
                : pendingAiFeedback?.error_type === 'layout'
                  ? 'layout'
                  : pendingAiFeedback?.error_type === 'grammar'
                    ? 'grammar'
                    : 'wrong'
          }
          aiHint={pendingAiFeedback?.hint}
          typoCorrection={typoHint || expectedAnswer}
        />
      )}

      {/* Успех */}
      {status === 'correct' && (
        <div style={{
          padding: '16px',
          background: 'rgba(16, 185, 129, 0.12)',
          border: '1px solid var(--theme-success)',
          borderRadius: 14,
          textAlign: 'center',
          color: 'var(--theme-success)',
          fontSize: 16,
          fontWeight: 700,
        }}>
          {hasMistake ? `✓ Теперь правильно: ${expectedAnswer}!` : `🎉 Отлично! Верно: ${expectedAnswer}`}
        </div>
      )}

      {/* Кнопка Далее при autoAdvance=false только после успешного правильного ввода */}
      {status === 'correct' && !autoAdvance && onAdvance && (
        <button
          type="button"
          onClick={onAdvance}
          style={{
            width: '100%',
            marginTop: 16,
            padding: '14px',
            background: 'var(--theme-primary)',
            color: 'var(--theme-primary-text)',
            border: 'none',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          Далее →
        </button>
      )}
    </div>
  );
}

// =====================
// Phrase Card (переведи предложение)
// =====================

interface AiPhraseValidation {
  is_correct: boolean;
  explanation: string;
  _ai_provider?: string;
}

function PhraseCard({ card, onAnswer, isDesktop = false, showText: initialShowText = true, onToggleShowText }: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean, userInput: string, isRetry?: boolean) => void;
  isDesktop?: boolean;
  showText?: boolean;
  onToggleShowText?: (showText: boolean) => void;
}) {
  const [input, setInput] = useState('');
  const [status, setStatus] = useState<'idle' | 'checking' | 'correct' | 'feedback'>('idle');
  const [aiResult, setAiResult] = useState<AiPhraseValidation | null>(null);
  const [showChat, setShowChat] = useState(false);
  const isRuEn = card.direction === 'translation_to_original';
  const [phraseEn, setPhraseEn] = useState(card.phrase_en || '');
  const [phraseRu, setPhraseRu] = useState(card.phrase_ru || '');
  const [loadingPhrase, setLoadingPhrase] = useState(!card.phrase_en);
  const [userAnswer, setUserAnswer] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { speak, speaking: isPlaying } = useTTS();
  const { notify, impact } = useHapticFeedback();
  const promptPhrase = isRuEn ? phraseRu : phraseEn;
  const targetPhrase = isRuEn ? phraseEn : phraseRu;
  const ttsLang = isRuEn ? 'ru-RU' : undefined;

  // Lazy loading: если phrase_en отсутствует — запрашиваем с бэкенда
  useEffect(() => {
    setInput('');
    setStatus('idle');
    setAiResult(null);
    setShowChat(false);
    setPhraseEn(card.phrase_en || '');
    setPhraseRu(card.phrase_ru || '');
    setLoadingPhrase(!card.phrase_en);

    if (!card.phrase_en) {
      api.post<{ phrase_en: string; phrase_ru: string }>('/study/generate-phrase', { word_id: card.word.id })
        .then(result => {
          setPhraseEn(result.phrase_en);
          setPhraseRu(result.phrase_ru);
          setLoadingPhrase(false);
        })
        .catch(() => {
          setLoadingPhrase(false);
        });
    }
  }, [card.word.id, card.phrase_en, card.phrase_ru]);

  const handleListen = () => {
    onToggleShowText?.(false);
    if (promptPhrase) speak(promptPhrase, ttsLang);
  };

  const handleShowText = () => {
    onToggleShowText?.(true);
  };

  // Быстрая локальная проверка (fallback если AI тормозит)
  const checkLocal = (userInput: string, correctPhrase: string) => {
    const normalizeClean = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/[.,!?;:"'']/g, '').replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
    const normalizeFull = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е').replace(/[.,!?;:"'']/g, '').replace(/\s*[\(\)]\s*/g, ' ').replace(/\s+/g, ' ').trim();
    const userClean = normalizeClean(userInput);
    const userFull = normalizeFull(userInput);
    const phraseClean = normalizeClean(correctPhrase);
    const phraseFull = normalizeFull(correctPhrase);
    return userClean === phraseClean || userFull === phraseFull || userClean === phraseFull;
  };

  const handleSubmit = async () => {
    if (status === 'checking' || loadingPhrase) return;
    if (!input.trim()) {
      if (status === 'feedback') {
        // Пустой ввод в feedback — просто очищаем, не показываем ошибку
        return;
      }
      notify('warning');
      setStatus('feedback');
      setInput('');
      return;
    }
    setStatus('checking');

    // Параллельно: AI validation (с таймаутом 3 сек) + локальная проверка
    const localCorrect = checkLocal(input, targetPhrase);

    let aiResult: AiPhraseValidation | null = null;
    try {
      aiResult = await Promise.race([
        api.post<AiPhraseValidation>('/ai/validate-phrase', {
          correct: targetPhrase,
          user: input.trim(),
        }),
        new Promise<AiPhraseValidation>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 3000)
        ),
      ]);
    } catch {
      // AI недоступен или таймаут — используем локальную проверку
      aiResult = { is_correct: localCorrect, explanation: '' };
    }

    setAiResult(aiResult);
    const isRetry = status === 'feedback';

    if (aiResult.is_correct) {
      setStatus('correct');
      if (isRetry) {
        impact('light');
      } else {
        notify('success');
      }
      playSuccessSound();
      onAnswer(true, input, isRetry);
    } else {
      // Неверно — показываем feedback, очищаем поле для retry
      setUserAnswer(input);
      setInput('');
      setStatus('feedback');
      notify('error');
      playErrorSound();
      if (!isRetry) {
        // Первый неправильный ответ — сообщаем родителю для статистики
        onAnswer(false, input, false);
      }
      // Retry wrong — не сообщаем родителю, просто остаёмся на карточке
    }
  };

  const handleRetry = () => {
    setUserAnswer('');
    setInput('');
    setStatus('idle');
    setAiResult(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSkip = () => {
    notify('error');
    onAnswer(false, userAnswer, true);
  };

  const showInput = status !== 'correct';
  const inputDisabled = status === 'checking';
  const anchorRef = useKeyboardScroll(showInput);

  return (
    <div style={{ padding: '20px 16px' }}>
      <div style={{ marginBottom: 12, fontSize: 11, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'center' }}>
        Фразы
      </div>

      <div style={{ background: 'var(--theme-bg-elevated)', borderRadius: 16, padding: '24px 20px', marginBottom: 20 }}>
        {/* Тумблер 👂/👀 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, justifyContent: 'center' }}>
          <button
            onClick={handleListen}
            disabled={isPlaying}
            style={{
              padding: '8px 14px',
              borderRadius: 20,
              border: '1px solid var(--theme-border)',
              background: !initialShowText ? 'var(--theme-primary)' : 'var(--theme-bg)',
              color: !initialShowText ? '#111' : 'var(--theme-text)',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: isPlaying ? 0.7 : 1,
            }}
          >
            {isPlaying ? '🔊' : '👂'} Слушай
          </button>
          <button
            onClick={handleShowText}
            style={{
              padding: '8px 14px',
              borderRadius: 20,
              border: '1px solid var(--theme-border)',
              background: initialShowText ? 'var(--theme-primary)' : 'var(--theme-bg)',
              color: initialShowText ? '#111' : 'var(--theme-text)',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            👀 Читай
          </button>
        </div>

        {loadingPhrase && (
          <div style={{ fontSize: 14, color: 'var(--theme-text-muted)', textAlign: 'center', padding: '12px 0' }}>
            ⏳ Готовим пример...
          </div>
        )}
        {!loadingPhrase && initialShowText && (
          <div style={{ fontSize: 18, color: 'var(--theme-text)', lineHeight: 1.5, marginBottom: 8, textAlign: 'center' }}>
            {promptPhrase}
          </div>
        )}
        {!loadingPhrase && !initialShowText && (
          <div style={{ fontSize: 14, color: 'var(--theme-text-muted)', textAlign: 'center', fontStyle: 'italic' }}>
            {isRuEn ? 'Слушай предложение и переведи на английский...' : 'Слушай предложение и переведи...'}
          </div>
        )}
      </div>

      {showInput && (
        <div style={{ marginBottom: 12 }}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => !inputDisabled && setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                if (!inputDisabled && !loadingPhrase) handleSubmit();
              }
            }}
            placeholder={loadingPhrase ? 'Готовим пример...' : status === 'feedback' ? 'Введите правильный перевод...' : 'Введите перевод...'}
            autoFocus
            disabled={inputDisabled || loadingPhrase}
            style={{
              width: '100%', padding: '14px',
              background: status === 'feedback' ? 'rgba(255,82,82,0.08)' : 'var(--theme-bg-elevated)',
              border: `1px solid ${status === 'feedback' ? 'var(--theme-error)' : 'var(--theme-border)'}`,
              borderRadius: 10, color: 'var(--theme-text)',
              fontSize: 18, outline: 'none', textAlign: 'center', boxSizing: 'border-box',
              opacity: inputDisabled ? 0.6 : 1,
            }}
          />
        </div>
      )}

      {status === 'feedback' && (
        <div style={{ marginBottom: 16 }}>
          {/* Объяснение AI или fallback */}
          <div style={{
            padding: '14px 16px', marginBottom: 12,
            background: 'rgba(255, 82, 82, 0.08)',
            border: '1px solid rgba(255, 82, 82, 0.4)',
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--theme-error)' }}>
              ❌ Неверный перевод
            </div>
            {aiResult?.explanation ? (
              <div style={{ fontSize: 14, color: 'var(--theme-text)', lineHeight: 1.5, marginTop: 6 }}>{aiResult.explanation}</div>
            ) : (
              <div style={{ fontSize: 14, color: 'var(--theme-text)', lineHeight: 1.5, marginTop: 6 }}>
                Твой ответ не совпадает с правильным переводом.
              </div>
            )}
          </div>

          {/* Твой ответ */}
          {userAnswer && (
            <div style={{
              padding: '10px 14px', marginBottom: 12,
              background: 'var(--theme-bg-elevated)', border: '1px solid var(--theme-border)',
              borderRadius: 10, fontSize: 14, color: 'var(--theme-text-muted)',
            }}>
              <div>Твой ответ: <span style={{ fontWeight: 600, color: 'var(--theme-text)' }}>{userAnswer}</span></div>
            </div>
          )}

          {/* Правильный ответ */}
          <div style={{
            padding: '10px 14px', marginBottom: 12,
            background: 'rgba(76, 175, 80, 0.08)', border: '1px solid rgba(76, 175, 80, 0.4)',
            borderRadius: 10, fontSize: 14, color: 'var(--theme-success)',
          }}>
            <div>Правильный перевод: <span style={{ fontWeight: 600 }}>{targetPhrase}</span></div>
          </div>

          {/* Кнопка Попробовать ещё раз */}
          <button
            onClick={handleRetry}
            style={{
              width: '100%', padding: '14px',
              background: 'var(--theme-bg-elevated)',
              color: 'var(--theme-primary)',
              border: '1px solid var(--theme-primary)',
              borderRadius: 10,
              fontSize: 15, fontWeight: 600,
              cursor: 'pointer', minHeight: '44px',
              marginBottom: 12,
            }}
          >
            ↺ Попробовать ещё раз
          </button>
        </div>
      )}

      {status === 'correct' && (
        <div style={{
          padding: 16, background: 'rgba(0, 230, 118, 0.12)', border: '1px solid var(--theme-success)',
          borderRadius: 12, textAlign: 'center', marginBottom: 12,
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--theme-success)' }}>
            ✅ Правильно!
          </div>
          <div style={{ fontSize: 14, color: 'var(--theme-text)', marginTop: 6 }}>
            {targetPhrase}
          </div>
        </div>
      )}

      {(status === 'correct' || status === 'feedback') && !showChat && (
        <button
          onClick={() => setShowChat(true)}
          style={{
            width: '100%', padding: '14px',
            background: 'var(--theme-bg-elevated)',
            color: 'var(--theme-primary)',
            border: '1px solid var(--theme-primary)',
            borderRadius: 10,
            fontSize: 15, fontWeight: 600,
            cursor: 'pointer', minHeight: '44px',
            marginBottom: 12,
          }}
        >
          💬 Обсудить с AI
        </button>
      )}

      {showChat && (
        <AiChatBot
          inline
          onClose={() => setShowChat(false)}
          context={isRuEn
            ? `Пользователь переводит с русского на английский.\nРусское: "${phraseRu}"\nПравильный перевод: "${phraseEn}"\nПользователь написал: "${input.trim()}"\nРезультат: ${status === 'correct' ? '✅ Правильно' : '❌ Неверно'}\n\nОбъясняй перевод, разбирай нюансы, отвечай на вопросы пользователя по этому предложению. Будь дружелюбным и по делу. Не повторяй что "всё правильно" — если результат правильный, спрашивай чего непонятно.`
            : `Пользователь учит английское предложение.\nАнглийское: "${phraseEn}"\nПравильный перевод: "${phraseRu}"\nПользователь написал: "${input.trim()}"\nРезультат: ${status === 'correct' ? '✅ Правильно' : '❌ Неверно'}\n\nОбъясняй перевод, разбирай нюансы, отвечай на вопросы пользователя по этому предложению. Будь дружелюбным и по делу. Не повторяй что "всё правильно" — если результат правильный, спрашивай чего непонятно.`}
          initialMessage={status === 'correct'
            ? (isRuEn
              ? `✅ Отлично! Перевод "${phraseRu}" на английский верен.\n\nЕсть вопросы по этому предложению? Могу разобрать нюансы, грамматику или синонимы. 🦊🦝`
              : `✅ Отлично! Предложение "${phraseEn}" переведено верно.\n\nЕсть вопросы по этому предложению? Могу разобрать нюансы, грамматику или синонимы. 🦊🦝`)
            : (isRuEn
              ? `❌ Предложение: "${phraseRu}"\nПравильный перевод: "${phraseEn}"\n\n${aiResult?.explanation ? aiResult.explanation + '\n\n' : ''}Задай вопрос — разберём, почему так переводится. 🦊🦝`
              : `❌ Предложение: "${phraseEn}"\nПравильный перевод: "${phraseRu}"\n\n${aiResult?.explanation ? aiResult.explanation + '\n\n' : ''}Задай вопрос — разберём, почему так переводится. 🦊🦝`)
          }
        />
      )}

      {showInput && (
        <button
          onClick={handleSubmit}
          disabled={inputDisabled || loadingPhrase}
          style={{
            width: '100%', padding: '14px',
            background: (inputDisabled || loadingPhrase) ? 'var(--theme-border)' : 'var(--theme-primary)',
            color: (inputDisabled || loadingPhrase) ? 'var(--theme-text)' : '#111', border: 'none', borderRadius: 10,
            fontSize: 16, fontWeight: 600,
            cursor: (inputDisabled || loadingPhrase) ? 'not-allowed' : 'pointer', minHeight: '44px',
          }}
        >
          {loadingPhrase ? 'Готовим пример...' : status === 'checking' ? 'Проверяю...' : 'Проверить'}
        </button>
      )}
      <div ref={anchorRef} style={{ height: 20 }} />
    </div>
  );
}

// =====================
// Blank Card (вставь слово в предложение)
// =====================

function BlankCard({ card, onAnswer, isDesktop = false }: {
  card: StudyCard;
  onAnswer: (isCorrect: boolean) => void;
  isDesktop?: boolean;
}) {
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useKeyboardScroll(!submitted);
  const { notify } = useHapticFeedback();

  // Сброс при смене слова — критично, т.к. компонент может переиспользоваться
  useEffect(() => {
    setInput('');
    setSubmitted(false);
    setIsCorrect(null);
  }, [card.word.id]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleSubmit = () => {
    if (submitted && isCorrect === true) return; // уже правильно ответили

    const trimmed = input.trim();

    if (!trimmed) {
      if (submitted && isCorrect === false) return; // уже показали ошибку, ждём ввода
      // Первый пустой Enter — считаем ошибкой, показываем правильный ответ
      setIsCorrect(false);
      setSubmitted(true);
      notify('error');
      playErrorSound();
      return;
    }

    const correct = checkAnswer(trimmed, card.word.original_text);
    setIsCorrect(correct);
    setSubmitted(true);
    notify(correct ? 'success' : 'error');
    if (correct) {
      playSuccessSound();
      setTimeout(() => onAnswer(true), 300);
    } else {
      playErrorSound();
      // Ошибка: показываем правильный ответ, не уходим на следующую карточку
      // input остаётся доступным для повторной попытки
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit();
    }
  };

  const sentence = submitted ? card.sentence_en : card.sentence_en_blanked;
  const statusColor = submitted
    ? (isCorrect ? 'var(--theme-success)' : 'var(--theme-error)')
    : 'var(--theme-primary)';

  // Подавляем предупреждение о неиспользуемом isDesktop
  void isDesktop;

  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Заголовок */}
      <div style={{ textAlign: 'center', color: 'var(--theme-text-secondary)', fontSize: 13, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        📝 Вставь слово
      </div>

      {/* Английское предложение с пропуском */}
      <div style={{
        padding: '20px 16px',
        background: 'var(--theme-bg-card)',
        border: `2px solid ${statusColor}`,
        borderRadius: 16,
        textAlign: 'center',
        fontSize: 18,
        lineHeight: 1.6,
        color: 'var(--theme-text)',
        fontStyle: 'italic',
        transition: 'border-color 0.3s',
        minHeight: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {sentence || card.sentence_en_blanked}
      </div>

      {/* Русский контекст */}
      {card.sentence_ru && (
        <div style={{
          padding: '10px 14px',
          background: 'var(--theme-bg-elevated)',
          borderRadius: 10,
          fontSize: 13,
          color: 'var(--theme-text-secondary)',
          textAlign: 'center',
        }}>
          🇷🇺 {card.sentence_ru}
        </div>
      )}

      {/* Результат */}
      {submitted && (
        <div style={{
          textAlign: 'center',
          fontWeight: 700,
          fontSize: 16,
          color: isCorrect ? 'var(--theme-success)' : 'var(--theme-error)',
        }}>
          {isCorrect ? '✓ Правильно!' : (
            <div>
              <div>✗ Правильный ответ: {card.word.original_text}</div>
              <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--theme-text-secondary)', marginTop: 4 }}>
                Введи правильное слово выше и нажми →
              </div>
            </div>
          )}
        </div>
      )}

      {/* Поле ввода — показываем пока не ответили правильно */}
      {(!submitted || !isCorrect) && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Введи пропущенное слово..."
            style={{
              flex: 1,
              padding: '14px 16px',
              background: 'var(--theme-bg-card)',
              border: '1.5px solid var(--theme-border)',
              borderRadius: 12,
              color: 'var(--theme-text)',
              fontSize: 16,
              outline: 'none',
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim()}
            style={{
              padding: '14px 20px',
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text)',
              border: 'none',
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              cursor: input.trim() ? 'pointer' : 'not-allowed',
              opacity: input.trim() ? 1 : 0.5,
            }}
          >
            →
          </button>
        </div>
      )}
      <div ref={anchorRef} style={{ height: 20 }} />
    </div>
  );
}

// =====================
// Session Complete
// =====================

function SessionComplete({
  correctCount,
  wrongCount,
  xpGained,
  maxCombo = 0,
  onExit,
  hasMore,
  totalEligible,
  cardsAnswered,
  onContinue,
  transitionedWords = [],
  courseId,
  lessonIndex,
  courseStep,
  onStartStep2,
  onClaimReward,
  isGiftClaimed,
  isClaiming,
  onNextLesson,
}: {
  correctCount: number;
  wrongCount: number;
  xpGained: number;
  maxCombo?: number;
  onExit: () => void;
  hasMore?: boolean;
  totalEligible?: number;
  cardsAnswered?: number;
  onContinue?: () => void;
  transitionedWords?: string[];
  courseId?: number | null;
  lessonIndex?: number | null;
  courseStep?: 'mc' | 'input' | null;
  onStartStep2?: () => void;
  onClaimReward?: () => void;
  isGiftClaimed?: boolean;
  isClaiming?: boolean;
  onNextLesson?: () => void;
}) {
  const total = correctCount + wrongCount;
  const accuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const answered = cardsAnswered ?? total;
  const remaining = totalEligible !== undefined && answered !== undefined ? Math.max(0, totalEligible - answered) : 0;

  const tCount = transitionedWords.length;

  // Строка прогресса
  const progressText = totalEligible !== undefined && totalEligible > 0
    ? `Пройдено ${answered} из ${totalEligible} карточек`
    : answered > 0
      ? `Пройдено ${answered} карточек`
      : 'Начата сессия';

  // Статистика для вертикальной сетки
  const statRows = [
    { icon: '📊', label: progressText, value: '', wide: true },
    { icon: '✅', label: 'Верно', value: String(correctCount), color: 'var(--theme-success)' },
    { icon: '❌', label: 'Ошибок', value: String(wrongCount), color: 'var(--theme-error)' },
    { icon: '🎯', label: 'Точность', value: `${accuracy}%`, color: 'var(--theme-primary)' },
    ...(maxCombo >= 2 ? [{ icon: '🔥', label: 'Лучший комбо', value: `x${maxCombo}`, color: '#f59e0b' }] : []),
    ...(xpGained > 0 ? [{ icon: '⭐', label: 'XP', value: `+${xpGained}`, color: '#F59E0B' }] : []),
  ];

  const isCourseLesson = Boolean(courseId && lessonIndex);
  const isCourseStep1 = isCourseLesson && courseStep === 'mc';

  return (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      {/* Эмодзи + заголовок */}
      <div style={{ fontSize: 52, marginBottom: 16 }}>
        {isCourseStep1 ? '🎯' : isCourseLesson ? '🏆' : accuracy >= 80 ? '🎉' : accuracy >= 50 ? '💪' : '📚'}
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--theme-text)', marginBottom: isCourseStep1 ? 8 : 20 }}>
        {isCourseStep1
          ? `Урок ${lessonIndex}: Шаг 1 (Тест) пройден!`
          : isCourseLesson
            ? `Урок ${lessonIndex} полностью завершён!`
            : 'Сессия завершена!'}
      </h2>

      {/* Поясняющий подзаголовок для Шага 1 */}
      {isCourseStep1 && (
        <div style={{ fontSize: 14, color: 'var(--theme-text-secondary)', marginBottom: 20, maxWidth: 320, marginInline: 'auto' }}>
          Слова распознаны! Теперь закрепим их написание на клавиатуре: Шаг 2 (Ввод) ✍️
        </div>
      )}

      {/* Поясняющий подзаголовок для Шага 2 (Ввод) */}
      {isCourseLesson && !isCourseStep1 && (
        <div style={{
          fontSize: 13,
          color: 'var(--theme-text-secondary)',
          marginBottom: 20,
          background: 'var(--theme-bg-secondary)',
          padding: '8px 16px',
          borderRadius: 12,
          display: 'inline-block',
        }}>
          ✨ Все слова сданы чистым вводом! {wrongCount > 0 ? `Ошибки первого круга (${wrongCount}) отработаны на 100%.` : 'Идеально с первого раза!'}
        </div>
      )}

      {/* Интерактивный сундук за завершение урока */}
      {isCourseLesson && !isCourseStep1 && onClaimReward && (
        <div style={{ maxWidth: 340, margin: '0 auto 28px' }}>
          {!isGiftClaimed ? (
            <div style={{
              background: 'linear-gradient(180deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.06) 100%)',
              border: '2px solid rgba(245, 158, 11, 0.45)',
              borderRadius: 20,
              padding: '24px 20px',
              textAlign: 'center',
              boxShadow: '0 8px 30px rgba(245, 158, 11, 0.25)',
              position: 'relative',
            }}>
              <div
                onClick={onClaimReward}
                style={{
                  fontSize: 60,
                  marginBottom: 10,
                  lineHeight: 1,
                  display: 'inline-block',
                  cursor: isClaiming ? 'wait' : 'pointer',
                  transform: 'scale(1)',
                  transition: 'transform 0.2s ease',
                }}
              >
                🎁
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--theme-text)', marginBottom: 4 }}>
                Трофей за Урок {lessonIndex}
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 18, lineHeight: 1.4 }}>
                Внутри: <strong style={{ color: '#f59e0b' }}>+50 XP</strong> герою и сюрприз от 2EnBota 🦊🦝
              </div>

              <button
                onClick={onClaimReward}
                disabled={isClaiming}
                style={{
                  width: '100%',
                  padding: '15px 20px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 14,
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: isClaiming ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 6px 20px rgba(245, 158, 11, 0.4)',
                }}
              >
                <span>✨</span>
                <span>{isClaiming ? 'Открываем сундук...' : 'Открыть сундук с наградой'}</span>
                <span>✨</span>
              </button>
            </div>
          ) : (
            <div style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '2px solid rgba(16, 185, 129, 0.35)',
              borderRadius: 20,
              padding: '18px 20px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)',
            }}>
              <div style={{ fontSize: 42, marginBottom: 6 }}>✨🏆✨</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--theme-success)', marginBottom: 4 }}>
                Трофей за Урок {lessonIndex} открыт!
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
                Награда <strong style={{ color: '#f59e0b' }}>+50 XP</strong> начислена вашему герою
              </div>
            </div>
          )}
        </div>
      )}

      {/* Виджет дневной нормы (спидометр) */}
      <DailyGoalVictoryWidget cardsAnsweredInSession={answered} />

      {/* RPG XP Bar */}
      <RpgProgressBar xpGained={xpGained} />

      {/* Вертикальная сетка статистики */}
      <div style={{ display: 'grid', gap: 12, maxWidth: 320, margin: '0 auto 32px' }}>
        {statRows.map((row, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 14px', background: 'var(--theme-bg-elevated)', borderRadius: 10,
          }}>
            <span style={{ fontSize: 14, color: 'var(--theme-text-secondary)' }}>{row.icon} {row.label}</span>
            {row.wide ? null : (
              <span style={{ fontSize: 16, fontWeight: 700, color: row.color || 'var(--theme-text)' }}>{row.value}</span>
            )}
          </div>
        ))}
      </div>

      {/* Слова перешли в следующий режим */}
      {tCount > 0 && (
        <div style={{
          padding: '12px 14px', marginBottom: 16,
          background: 'rgba(0, 230, 118, 0.06)',
          border: '1px solid rgba(0, 230, 118, 0.25)',
          borderRadius: 10, textAlign: 'left', maxWidth: 320, marginInline: 'auto',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-success)', marginBottom: 6 }}>
            🎓 {tCount} {tCount === 1 ? 'слово изучено' : tCount < 5 ? 'слова изучены' : 'слов изучено'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
            {transitionedWords.map((w, i) => (
              <span key={i} style={{
                padding: '2px 8px', background: 'rgba(0, 230, 118, 0.12)',
                borderRadius: 6, fontSize: 12, fontWeight: 600, color: 'var(--theme-text)',
              }}>
                {w}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>
            {tCount === 1 ? 'Оно перешло в следующий режим' : 'Они перешли в следующий режим'}
          </div>
        </div>
      )}

      {/* Кнопки действий */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320, margin: '0 auto' }}>
        {/* Кнопка перехода к Шагу 2 (Ввод) */}
        {isCourseStep1 && onStartStep2 && (
          <button
            onClick={onStartStep2}
            style={{
              width: '100%',
              padding: '16px',
              background: 'var(--theme-primary)',
              color: 'var(--theme-primary-text)',
              border: 'none',
              borderRadius: 14,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            }}
          >
            ✍️ Перейти к Вводу (Шаг 2) →
          </button>
        )}

        {/* Кнопка перехода к следующему уроку: доступна ТОЛЬКО после открытия сундука! */}
        {isCourseLesson && !isCourseStep1 && onNextLesson && (
          isGiftClaimed ? (
            <button
              onClick={onNextLesson}
              style={{
                width: '100%',
                padding: '16px',
                background: 'var(--theme-primary)',
                color: 'var(--theme-primary-text)',
                border: 'none',
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              }}
            >
              🚀 Перейти к Уроку {lessonIndex! + 1} →
            </button>
          ) : (
            <div style={{
              padding: '14px',
              background: 'var(--theme-bg-elevated)',
              border: '1px dashed rgba(245, 158, 11, 0.4)',
              borderRadius: 14,
              fontSize: 13,
              color: 'var(--theme-text-secondary)',
              lineHeight: 1.4,
            }}>
              👆 <strong>Откройте сундук выше</strong>, чтобы забрать трофей и открыть следующий урок!
            </div>
          )
        )}

        {onContinue && !isCourseLesson && (
          <button onClick={onContinue} style={{
            width: '100%', padding: '14px', background: 'var(--theme-primary)', color: 'var(--theme-primary-text)',
            border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
          }}>
            {hasMore && remaining > 0 ? 'Продолжить' : 'Продолжить учёбу'}
          </button>
        )}

        <button onClick={onExit} style={{
          width: '100%', padding: '14px', background: 'var(--theme-bg-elevated)', color: 'var(--theme-text-secondary)',
          border: '1px solid var(--theme-border)', borderRadius: 12, fontSize: 16, cursor: 'pointer',
        }}>
          {isCourseLesson ? '← Вернуться к списку уроков' : 'Выйти'}
        </button>
      </div>
    </div>
  );
}

// =====================
// Main Study Page
// =====================

export function StudyPage() {
  const navigate = useNavigate();
  const { autoAdvance, cardNavHotkeys } = useSettingsStore();
  const { notify, impact } = useHapticFeedback();
  const isDesktop = useIsDesktop();
  const [searchParams] = useSearchParams();

  // Режим курса или папки: если передан course_id / folder_id, изучаем фильтрованное подмножество
  const courseId = searchParams.get('course_id') ? parseInt(searchParams.get('course_id')!, 10) : null;
  const courseName = searchParams.get('course_name') ? decodeURIComponent(searchParams.get('course_name')!) : null;
  const lesson = searchParams.get('lesson') ? parseInt(searchParams.get('lesson')!, 10) : null;
  const folderId = searchParams.get('folder_id') ? parseInt(searchParams.get('folder_id')!, 10) : null;
  const folderName = searchParams.get('folder_name') ? decodeURIComponent(searchParams.get('folder_name')!) : null;

  const [session, setSession] = useState<StudySession | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [totalXp, setTotalXp] = useState(0);
  const [wordCounts, setWordCounts] = useState<any>(null);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [showGift, setShowGift] = useState(false);
  const [lootDrop, setLootDrop] = useState<AnswerResult['loot_drop'] | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [mcReps, setMcReps] = useState(1);
  const [pendingAdvance, setPendingAdvance] = useState(false);
  // Результат retry для InputCard: 'reset' если слово вернули в MC, 'wrong' если просто ошибка
  const [retryResult, setRetryResult] = useState<'reset' | 'wrong' | null>(null);
  // AI feedback для InputCard: когда сервер вернул awaiting_retry, передаём сюда данные
  const [pendingAiFeedback, setPendingAiFeedback] = useState<AiFeedback | null>(null);
  // Текущий offset для MC батчей
  const [currentOffset, setCurrentOffset] = useState(0);
  // Режим отображения фраз: true = показывать текст (👀), false = слушать (👂)
  const [phraseShowText, setPhraseShowText] = useState(true);
  // Отвеченные карточки (предотвращает double-answer через хоткеи)
  const [answeredCardKeys, setAnsweredCardKeys] = useState<Set<string>>(new Set());
  // Направление перевода для тренировочных режимов
  const [direction, setDirection] = useState<'original_to_translation' | 'translation_to_original'>('original_to_translation');
  // Счётчик карточек прошедших в рамках всех батчей (для отображения прогресса)
  const [totalCardsAnswered, setTotalCardsAnswered] = useState(0);
  const [wordMastered, setWordMastered] = useState(false);
  const [transitionedWords, setTransitionedWords] = useState<string[]>([]);
  const [isGiftClaimed, setIsGiftClaimed] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [initialCardsCount, setInitialCardsCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [xpFloat, setXpFloat] = useState<number | null>(null);
  const [isCardShaking, setIsCardShaking] = useState(false);

  const handleClaimLessonReward = async () => {
    if (!courseId || !lesson || isClaiming || isGiftClaimed) return;
    setIsClaiming(true);
    try {
      const res = await api.post<{ xp_gained: number; loot_drop: any }>(
        `/courses/${courseId}/lessons/${lesson}/reward`,
        {}
      );
      setTotalXp(x => x + (res.xp_gained ?? 50));
      setLootDrop(res.loot_drop ?? null);
      setIsGiftClaimed(true);
      setTimeout(() => setShowGift(true), 150);
      useRpgStore.getState().load();
    } catch (err) {
      console.error('Ошибка получения награды:', err);
      setIsGiftClaimed(true);
      setShowGift(true);
    } finally {
      setIsClaiming(false);
    }
  };

  const handleStartStep2 = useCallback(() => {
    if (!courseId || !lesson) return;
    setIsComplete(false);
    setCorrectCount(0);
    setWrongCount(0);
    setTotalXp(0);
    setInitialCardsCount(0);
    setAnsweredCardKeys(new Set());
    setTransitionedWords([]);
    setCombo(0);
    setMaxCombo(0);
    setXpFloat(null);
    setIsCardShaking(false);
    startSession('input', 0, lesson);
  }, [courseId, lesson]);

  const handleNextLesson = useCallback(() => {
    if (!courseId || !lesson) return;
    const nextL = lesson + 1;
    setIsComplete(false);
    setCorrectCount(0);
    setWrongCount(0);
    setTotalXp(0);
    setIsGiftClaimed(false);
    setInitialCardsCount(0);
    setAnsweredCardKeys(new Set());
    setTransitionedWords([]);
    setCombo(0);
    setMaxCombo(0);
    setXpFloat(null);
    setIsCardShaking(false);
    navigate(`/study?course_id=${courseId}&course_name=${encodeURIComponent(courseName || '')}&mode=mc&lesson=${nextL}`, { replace: true });
    startSession('mc', 0, nextL);
  }, [courseId, lesson, courseName, navigate]);

  const loadWordCounts = useCallback(() => {
    let url: string;
    if (courseId) {
      url = `/courses/${courseId}/study-stats`;
    } else if (folderId) {
      url = `/folders/${folderId}/study-stats`;
    } else {
      url = '/words/count';
    }
    api.get<any>(url).then(setWordCounts).catch((err) => {
      console.error(err);
      captureError(err, { context: 'StudyPage.wordCount' });
    });
  }, [courseId, folderId]);

  useEffect(() => {
    loadWordCounts();
  }, [loadWordCounts]);

  // Автостарт сессии из query param ?mode=... (deep link с HomePage или CoursesPage)
  const autoStartRef = useRef(false);
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam && !autoStartRef.current) {
      autoStartRef.current = true;
      startSession(modeParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startSession = useCallback(async (mode: string, offset: number = 0, overrideLesson?: number) => {
    // Guided tour: auto-advance после выбора MC
    if (mode === 'mc' && useGuidedTourStore.getState().step === 'select-mc') {
      useGuidedTourStore.getState().nextStep();
    }
    // Input v2: бессессионный режим, не нужно грузить карточки (только для глобального режима словаря)
    if (mode === 'input' && !courseId && !folderId) {
      setSelectedMode('input');
      return;
    }
    // SRS/Повторение: используем InputModeView с srsOnly=true (печать вместо MC-выбора)
    if (mode === 'srs' && !courseId && !folderId) {
      setSelectedMode('srs');
      return;
    }
    setIsLoading(true);
    setSelectedMode(mode);
    try {
      let url: string;
      const dirParam = direction && direction !== 'original_to_translation' ? `&direction=${direction}` : '';
      if (courseId) {
        const targetLesson = overrideLesson ?? lesson;
        const lessonParam = targetLesson ? `&lesson=${targetLesson}` : '';
        url = offset > 0
          ? `/courses/${courseId}/study?mode=${mode}&offset=${offset}${dirParam}${lessonParam}`
          : `/courses/${courseId}/study?mode=${mode}${dirParam}${lessonParam}`;
      } else if (folderId) {
        url = offset > 0
          ? `/study/cards?mode=${mode}&offset=${offset}&folder_id=${folderId}${dirParam}`
          : `/study/cards?mode=${mode}&folder_id=${folderId}${dirParam}`;
      } else {
        url = offset > 0
          ? `/study/cards?mode=${mode}&offset=${offset}${dirParam}`
          : `/study/cards?mode=${mode}${dirParam}`;
      }
      const data = await api.get<StudySession>(url);
      if (data.cards.length === 0) {
        if (courseId && lesson) {
          setIsComplete(true);
          return;
        }
        setToastMessage('В этом режиме нет доступных карточек. Добавьте слова!');
        setSelectedMode(null);
        return;
      }
      setSession(data);
      setCurrentIndex(0);
      setIsComplete(false);
      setCurrentOffset(offset);
      setWordMastered(false);
      setPhraseShowText(true); // Новая сессия — показываем текст по умолчанию
      if (offset === 0) {
        // Новая сессия — сбрасываем статистику
        setCorrectCount(0);
        setWrongCount(0);
        setTotalXp(0);
        setTotalCardsAnswered(0);
        setTransitionedWords([]);
        setAnsweredCardKeys(new Set());
        setInitialCardsCount(data.cards.length);
        setCombo(0);
        setMaxCombo(0);
        setXpFloat(null);
        setIsCardShaking(false);
      }
      const cardsWithCtx = data.cards.filter((c: any) => c.context_sentence).length;
      const cardsWithoutCtx = data.cards.filter((c: any) => !c.context_sentence).length;
      const sampleCard = data.cards[0] as any;
      trackEvent('study_session_started', {
        mode, cards_count: data.cards.length, offset, course_id: courseId,
        cards_with_context: cardsWithCtx,
        cards_without_context: cardsWithoutCtx,
        sample_has_context: !!sampleCard?.context_sentence,
        sample_word: sampleCard?.word?.original_text,
        sample_context_keys: sampleCard?.context_sentence ? Object.keys(sampleCard.context_sentence) : null,
        card_keys: sampleCard ? Object.keys(sampleCard) : null,
      });
    } catch (err: any) {
      captureError(err, { context: 'StudyPage.startSession', mode });
      setToastMessage(err.message || 'Ошибка загрузки карточек');
      setSelectedMode(null);
    } finally {
      setIsLoading(false);
    }
  }, [courseId, direction]);

  // Перезагрузка сессии с новым направлением, сохраняя позицию и статистику
  const reloadSession = useCallback(async (
    newDir: 'original_to_translation' | 'translation_to_original',
    keepIndex: number,
    keepPhraseShowText?: boolean,
  ) => {
    if (!selectedMode) return;
    setIsLoading(true);
    try {
      const dirParam = newDir !== 'original_to_translation' ? `&direction=${newDir}` : '';
      const lessonParam = lesson ? `&lesson=${lesson}` : '';
      const url = courseId
        ? `/courses/${courseId}/study?mode=${selectedMode}${dirParam}${lessonParam}`
        : `/study/cards?mode=${selectedMode}${dirParam}`;
      const data = await api.get<StudySession>(url);
      if (data.cards.length === 0) {
        setToastMessage('Нет карточек для этого режима. Добавьте слова!');
        return;
      }
      setSession(data);
      setCurrentIndex(Math.min(keepIndex, data.cards.length - 1));
      setIsComplete(false);
      setWordMastered(false);
      setPendingAdvance(false);
      if (keepPhraseShowText !== undefined) setPhraseShowText(keepPhraseShowText);
      setRetryResult(null);
      setPendingAiFeedback(null);
    } catch (err: any) {
      captureError(err, { context: 'StudyPage.reloadSession', mode: selectedMode });
      setToastMessage(err.message || 'Ошибка загрузки карточек');
    } finally {
      setIsLoading(false);
    }
  }, [courseId, selectedMode]);

  // Сбрасываем состояние при смене карточки
  useEffect(() => {
    setRetryResult(null);
    setWordMastered(false);
    setPendingAdvance(false);
  }, [currentIndex]);

  // Переключение направления перевода (EN→RU ↔ RU→EN)
  // Перезагружаем сессию с бэкенда, сохраняя текущую позицию, статистику и режим 👂/👀
  const handleToggleDirection = useCallback(async () => {
    if (!selectedMode || !session) return;
    const newDir = direction === 'original_to_translation' ? 'translation_to_original' : 'original_to_translation';
    const savedIndex = currentIndex;
    const savedPhraseShowText = phraseShowText;
    setDirection(newDir);
    await reloadSession(newDir, savedIndex, savedPhraseShowText);
  }, [direction, selectedMode, session, currentIndex, reloadSession, phraseShowText]);

  const handleAnswer = useCallback(async (isCorrect: boolean, userInput?: string, isRetry?: boolean, spareLife?: boolean, hasMistake?: boolean) => {
    if (!session) return;
    const card = session.cards[currentIndex];
    const cardKey = `${card.word.id}_${card.mode}_${card.direction}`;
    if (card.mode !== 'input' && !isRetry && answeredCardKeys.has(cardKey)) return; // уже отвечена — пропускаем, но retry разрешаем
    if (card.mode !== 'input') {
      setAnsweredCardKeys(prev => new Set(prev).add(cardKey));
    }

    try {
      // Режимы без серверного прогресса: blanked (auto-advance), audition (manual advance)
      if (card.mode === 'blanked' || card.mode === 'audition') {
        if (isCorrect) {
          setCombo(c => {
            const next = c + 1;
            setMaxCombo(m => Math.max(m, next));
            if (next >= 2) impact(next >= 10 ? 'heavy' : next >= 5 ? 'medium' : 'light');
            return next;
          });
        } else {
          setCombo(0);
          setIsCardShaking(true);
          setTimeout(() => setIsCardShaking(false), 500);
          notify('warning');
        }
        setCorrectCount(c => c + (isCorrect ? 1 : 0));
        setWrongCount(w => w + (isCorrect ? 0 : 1));
        setTotalCardsAnswered(n => n + 1);

        // blanked + audition: manual advance через кнопку "Далее"
        setPendingAdvance(true);
        return;
      }

      // Оптимистичное обновление кружков MC — мгновенно при правильном ответе
      if (isCorrect && (card.mode === 'mc' || card.mode === 'srs')) {
        // Guided tour: auto-advance после правильного MC-ответа
        if (useGuidedTourStore.getState().step === 'mc-card') {
          useGuidedTourStore.getState().nextStep();
        }
        setSession(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            cards: prev.cards.map(c =>
              c.word.id === card.word.id
                ? {
                    ...c,
                    word: {
                      ...c.word,
                      mc_en_ru_progress: c.word.mc_en_ru_progress + (card.direction === 'original_to_translation' ? 1 : 0),
                      mc_ru_en_progress: c.word.mc_ru_en_progress + (card.direction === 'translation_to_original' ? 1 : 0),
                    },
                  }
                : c
            ),
          };
        });
      }

      const answerUrl = courseId ? `/courses/${courseId}/answer` : '/study/answer';
      const result = await api.post<AnswerResult>(answerUrl, {
        word_id: card.word.id,
        mode: card.mode,
        direction: card.direction,
        is_correct: isCorrect,
        user_input: userInput,
        ...(isRetry ? { is_retry: true } : {}),
        ...(spareLife ? { spare_life: true } : {}),
        ...(hasMistake !== undefined ? { has_mistake: hasMistake } : {}),
      });

      trackEvent('study_answer_result', {
        word_id: card.word.id,
        word: card.word.original_text,
        mode: card.mode,
        direction: card.direction,
        is_correct: isCorrect,
        is_retry: isRetry || false,
        xp_gained: result.xp_gained || 0,
        new_srs_level: result.new_srs_level,
        transitioned_to_input: result.transitioned_to_input,
        word_reset_to_mc: result.word_reset_to_mc,
        awaiting_retry: result.awaiting_retry || false,
        card_index: currentIndex + 1,
        cards_total: session.cards.length,
      });

      // Если сервер вернул awaiting_retry — остаёмся на карточке (пользователь должен ввести правильный ответ!)
      if (result.awaiting_retry) {
        setCombo(0);
        setIsCardShaking(true);
        playErrorSound();
        setTimeout(() => setIsCardShaking(false), 500);
        notify('warning');
        const wrongKey = `${cardKey}_wrong`;
        if (!answeredCardKeys.has(wrongKey)) {
          setWrongCount(w => w + 1);
          setAnsweredCardKeys(prev => new Set(prev).add(wrongKey));
        }
        if (result.ai_feedback) {
          setPendingAiFeedback(result.ai_feedback);
        }
        return;
      }

      // Сбрасываем AI feedback после успешного ввода
      setPendingAiFeedback(null);

      if (isCorrect) {
        setCombo(c => {
          const next = c + 1;
          setMaxCombo(m => Math.max(m, next));
          if (next >= 2) impact(next >= 10 ? 'heavy' : next >= 5 ? 'medium' : 'light');
          return next;
        });
        if (result.xp_gained) {
          setXpFloat(result.xp_gained);
          setTimeout(() => setXpFloat(null), 1200);
        }
      } else {
        setCombo(0);
        setIsCardShaking(true);
        setTimeout(() => setIsCardShaking(false), 500);
        notify('warning');
      }

      // Фразы — чистая тренировка, без XP и обновления БД
      if (card.mode === 'phrases') {
        if (!isRetry) {
          // Первый ответ — всегда считаем
          setCorrectCount(c => c + (isCorrect ? 1 : 0));
          setWrongCount(w => w + (isCorrect ? 0 : 1));
          setTotalCardsAnswered(n => n + 1);
        } else if (isCorrect) {
          // Retry correct — засчитываем правильный, но totalCardsAnswered уже был на первом ответе
          setCorrectCount(c => c + 1);
        }
        // После любого ответа (правильного/неправильного/Далее) показываем Далее
        setPendingAdvance(true);
        return;
      }

      // Диктант — чистая тренировка, без XP и обновления БД
      // При ошибке остаёмся на карточке (awaiting_retry выше), при правильном — кнопка «Далее»
      if (card.mode === 'dictation') {
        setCorrectCount(c => c + (isCorrect ? 1 : 0));
        setWrongCount(w => w + (isCorrect ? 0 : 1));
        setTotalCardsAnswered(n => n + 1);
        if (isCorrect) {
          setPendingAdvance(true);
        }
        return;
      }

      // Если это retry — сигнализируем InputCard о результате (для wrong_final)
      if (isRetry && !result.is_correct) {
        setRetryResult(result.word_reset_to_mc ? 'reset' : 'wrong');
      }

      const hadPreviousMistake = Boolean(hasMistake) || answeredCardKeys.has(`${cardKey}_wrong`);
      if (!hadPreviousMistake && currentIndex < (initialCardsCount || session.cards.length)) {
        setCorrectCount(c => c + (isCorrect ? 1 : 0));
        setWrongCount(w => w + (isCorrect ? 0 : 1));
      }

      // Для режима Input: если ответ НЕ чистый (была ошибка/подсказка), отправляем карточку в штрафную очередь ошибок!
      const shouldRetryLater = card.mode === 'input' && hadPreviousMistake;
      if (shouldRetryLater) {
        setSession(prev => prev ? { ...prev, cards: [...prev.cards, card] } : null);
        // Снимаем метку wrongKey, чтобы на повторном круге карточка могла быть сдана начисто
        setAnsweredCardKeys(prev => {
          const next = new Set(prev);
          next.delete(`${cardKey}_wrong`);
          return next;
        });
      } else {
        setAnsweredCardKeys(prev => new Set(prev).add(cardKey));
      }

      // Перемешивание штрафных карточек при переходе к кругу «Работа над ошибками»
      if (
        card.mode === 'input' &&
        initialCardsCount > 0 &&
        currentIndex + 1 === initialCardsCount &&
        (session.cards.length + (shouldRetryLater ? 1 : 0)) > initialCardsCount
      ) {
        setSession(prev => {
          if (!prev) return prev;
          const head = prev.cards.slice(0, initialCardsCount);
          const tail = prev.cards.slice(initialCardsCount);
          for (let i = tail.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const tmp = tail[i];
            tail[i] = tail[j];
            tail[j] = tmp;
          }
          return { ...prev, cards: [...head, ...tail] };
        });
      }

      setTotalXp(xp => xp + (result.xp_gained || 0));
      setTotalCardsAnswered(n => n + 1);

      // Обновляем MC прогресс в session cards для корректного отображения кружков
      setSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          cards: prev.cards.map(c =>
            c.word.id === card.word.id
              ? {
                  ...c,
                  word: {
                    ...c.word,
                    mc_en_ru_progress: result.new_mc_en_ru_progress,
                    mc_ru_en_progress: result.new_mc_ru_en_progress,
                  },
                }
              : c
          ),
        };
      });

      // Loot drop — показываем подарок
      if (result.loot_drop) {
        setLootDrop(result.loot_drop);
        setTimeout(() => notify('success'), 250);
        setTimeout(() => setShowGift(true), 400);
      }

      const effectiveTotalCards = session.cards.length + (shouldRetryLater ? 1 : 0);
      const isLast = currentIndex + 1 >= effectiveTotalCards;

      // Слово изучено — показываем празднование внутри виджета
      if (result.transitioned_to_input) {
        setTimeout(() => impact('heavy'), 180);
        setWordMastered(true);
        setTransitionedWords(prev => [...prev, card.word.original_text]);
        if (autoAdvance) {
          // Даём пользователю увидеть празднование ~1.2с перед авто-переходом
          // wordMastered сбросится при смене карточки (useEffect на currentIndex)
          setTimeout(() => {
            if (isLast) {
              setIsComplete(true);
              useRpgStore.getState().load();
              const newCorrect = correctCount + (isCorrect ? 1 : 0);
              const newWrong = wrongCount + (isCorrect ? 0 : 1);
              trackEvent('study_session_completed', {
                mode: selectedMode,
                correct: newCorrect,
                wrong: newWrong,
                accuracy: Math.round((newCorrect / session.cards.length) * 100),
                xp_gained: totalXp + (result.xp_gained || 0),
                cards_count: session.cards.length,
              });
            } else {
              setCurrentIndex(i => i + 1);
            }
          }, 1200);
          return;
        } else {
          // Ручное управление: ждём нажатия «Далее»
          setPendingAdvance(true);
          return;
        }
      }

      if (isLast && autoAdvance) {
        // Автопереход: сразу завершаем сессию
        setIsComplete(true);
        useRpgStore.getState().load();
        const newCorrect = correctCount + (isCorrect ? 1 : 0);
        const newWrong = wrongCount + (isCorrect ? 0 : 1);
        trackEvent('study_session_completed', {
          mode: selectedMode,
          correct: newCorrect,
          wrong: newWrong,
          accuracy: Math.round((newCorrect / session.cards.length) * 100),
          xp_gained: totalXp + (result.xp_gained || 0),
          cards_count: session.cards.length,
        });
      } else if (!autoAdvance) {
        // Ручное управление: ждём нажатия «Далее» (в т.ч. на последней карточке)
        setPendingAdvance(true);
      } else {
        setCurrentIndex(i => i + 1);
      }
    } catch (err: any) {
      console.error('Answer error:', err);
      captureError(err, { context: 'StudyPage.handleAnswer' });
      setPendingAiFeedback(null);
      if (card.mode === 'dictation') {
        // При ошибке API в диктанте просто остаёмся на карточке, позволяем попробовать снова
        return;
      }
      if (card.mode === 'input' && !isCorrect) {
        const wrongKey = `${cardKey}_wrong`;
        if (!answeredCardKeys.has(wrongKey)) {
          setWrongCount(w => w + 1);
          setAnsweredCardKeys(prev => new Set(prev).add(wrongKey));
        }
        return;
      }
      if (currentIndex + 1 >= session.cards.length) {
        setIsComplete(true);
      } else if (!autoAdvance) {
        setPendingAdvance(true);
      } else {
        setCurrentIndex(i => i + 1);
      }
    }
  }, [session, currentIndex, correctCount, wrongCount, totalXp, selectedMode, autoAdvance, courseId, answeredCardKeys, notify, impact]);

  const handleManualAdvance = useCallback(() => {
    setPendingAdvance(false);
    setPendingAiFeedback(null);
    setRetryResult(null);
    setWordMastered(false);
    const isLast = session ? currentIndex + 1 >= session.cards.length : false;
    if (isLast) {
      setIsComplete(true);
      useRpgStore.getState().load();
      trackEvent('study_session_completed', {
        mode: selectedMode,
        correct: correctCount,
        wrong: wrongCount,
        accuracy: session ? Math.round((correctCount / session.cards.length) * 100) : 0,
        xp_gained: totalXp,
        cards_count: session?.cards.length ?? 0,
      });
    } else {
      setCurrentIndex(i => i + 1);
    }
  }, [session, currentIndex, correctCount, wrongCount, totalXp, selectedMode]);

  // Глобальный Enter для кнопки "Далее" на десктопе (только для non-mc/srs режимов)
  const globalEnterRef = useRef({ pendingAdvance, session, currentIndex, handleManualAdvance });
  globalEnterRef.current = { pendingAdvance, session, currentIndex, handleManualAdvance };
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.code !== 'NumpadEnter') return;
      // Не перехватываем Enter если фокус внутри AI-чата или в поле ввода
      const target = e.target as HTMLElement;
      if (target.closest('[data-chat-container]')) return;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return; // Input fields handle their own Enter
      const { pendingAdvance, session, currentIndex, handleManualAdvance } = globalEnterRef.current;
      if (!pendingAdvance || !session) return;
      const card = session.cards[currentIndex];
      if (card?.mode === 'mc' || card?.mode === 'srs') return; // These cards handle their own Enter/submit
      e.preventDefault();
      handleManualAdvance();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop]);

  // Хоткеи Ctrl+Left / Ctrl+Right для листания карточек
  useEffect(() => {
    if (!isDesktop || !cardNavHotkeys || !session) return;
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex(i => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex(i => Math.min(session.cards.length - 1, i + 1));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isDesktop, cardNavHotkeys, session]);

  // Предзагрузка phrase-примеров для следующих 2 карточек (lazy loading)
  useEffect(() => {
    if (!session) return;
    const preloadCount = 2;
    for (let i = 1; i <= preloadCount; i++) {
      const nextIdx = currentIndex + i;
      if (nextIdx >= session.cards.length) break;
      const nextCard = session.cards[nextIdx];
      if (nextCard.mode === 'phrases' && !nextCard.phrase_en) {
        api.post<{ phrase_en: string; phrase_ru: string }>('/study/generate-phrase', { word_id: nextCard.word.id })
          .then(result => {
            setSession(prev => {
              if (!prev) return prev;
              const newCards = [...prev.cards];
              newCards[nextIdx] = { ...newCards[nextIdx], phrase_en: result.phrase_en, phrase_ru: result.phrase_ru };
              return { ...prev, cards: newCards };
            });
          })
          .catch(() => { /* игнорируем ошибки предзагрузки */ });
      }
    }
  }, [session, currentIndex]);

  // После того как админ сохранил правки слова — обновляем текущую карточку.
  // Для MC дёргаем /study/regenerate-card (новые 4 варианта ответа).
  // Для остальных режимов (audition/dictation/phrases/blanked) — оптимистичный
  // патч main_translation + translations, чтобы хотя бы отображаемый перевод
  // был свежим. options в audition могут остаться устаревшими — это терпимо,
  // при следующем появлении карточки пересоздадутся.
  const handleCurrentCardRegenerate = useCallback(async (updated?: any) => {
    if (!session) return;
    const card = session.cards[currentIndex];
    if (!card) return;

    const isRegeneratable = card.mode === 'mc' || card.mode === 'srs';
    if (isRegeneratable) {
      try {
        const resp = await api.post<{ card: StudyCard }>('/study/regenerate-card', {
          word_id: card.word.id,
          mode: card.mode,
          direction: card.direction,
        });
        if (!resp?.card) return;
        setSession(prev => {
          if (!prev) return prev;
          const newCards = [...prev.cards];
          newCards[currentIndex] = resp.card;
          return { ...prev, cards: newCards };
        });
        return;
      } catch (err) {
        captureError(err, { context: 'study_regenerate_card' });
        // упадём в оптимистичный патч ниже
      }
    }

    // Оптимистичный патч для остальных режимов или fallback при ошибке regenerate
    if (!updated) return;
    setSession(prev => {
      if (!prev) return prev;
      const newCards = [...prev.cards];
      const c = newCards[currentIndex];
      newCards[currentIndex] = {
        ...c,
        word: {
          ...c.word,
          main_translation_text: updated.main_translation_text ?? c.word.main_translation_text,
          translations: (updated.translations ?? c.word.translations ?? []).map((t: any) => ({
            id: t.id,
            word_id: c.word.id,
            translation_text: t.translation_text,
          })),
        },
      };
      return { ...prev, cards: newCards };
    });
  }, [session, currentIndex]);

  const handleContinue = useCallback(() => {
    if (selectedMode && session) {
      const nextOffset = currentOffset + session.cards.length;
      startSession(selectedMode, nextOffset);
    }
  }, [selectedMode, session, currentOffset, startSession]);

  const [exitSummary, setExitSummary] = useState<{ correct: number; wrong: number; answered: number; transitionedWords: string[] } | null>(null);

  const handleExit = () => {
    // Для всех сессионных режимов (кроме input/srs которые рендерят InputModeView):
    // показываем окно завершения с возможностью продолжить
    const sessionModes = ['mc', 'phrases', 'dictation', 'audition', 'blanked'];
    if (session && sessionModes.includes(selectedMode ?? '') && !isComplete) {
      setExitSummary({ correct: correctCount, wrong: wrongCount, answered: totalCardsAnswered, transitionedWords });
      return;
    }
    setSession(null);
    setSelectedMode(null);
    setIsComplete(false);
    setWordMastered(false);
    setExitSummary(null);
    if (courseId) {
      navigate(`/courses?open=${courseId}`);
      return;
    }
    // Обновляем счётчики при возврате к ModeSelector
    loadWordCounts();
  };

  const handleExitSummaryClose = () => {
    setSession(null);
    setSelectedMode(null);
    setIsComplete(false);
    setWordMastered(false);
    setExitSummary(null);
    if (courseId) {
      navigate(`/courses?open=${courseId}`);
      return;
    }
    loadWordCounts();
  };

  const isTabActive = useIsTabActive();

  // Фоновое тихое обновление счётчиков слов при переходе на вкладку "Учёба"
  useOnTabActive(() => {
    loadWordCounts();
  });

  // Интеграция жеста назад (iOS swipe back) и кнопки BackButton Telegram
  useBackHandler(() => {
    if (exitSummary) {
      // Если окно паузы/подтверждения уже открыто — подтверждаем выход
      handleExitSummaryClose();
    } else if (isComplete) {
      handleExit();
    } else if (selectedMode) {
      // В активном обучении работает как крестик ✕ — ставит на паузу и показывает кнопку "Продолжить"
      handleExit();
    } else if (courseId) {
      navigate(`/courses?open=${courseId}`);
    } else {
      navigate(-1);
    }
  }, isTabActive);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--theme-text-secondary)' }}>
          Загрузка карточек...
        </div>
      </Layout>
    );
  }

  // Режим ВВОД v2 — бессессионный, отдельный flow ТОЛЬКО для общего словаря (не в курсах!)
  if (selectedMode === 'input' && !courseId && !folderId) {
    return (
      <Layout showNav={false}>
        <InputModeView onExit={handleExit} />
      </Layout>
    );
  }

  // Режим ПОВТОРЕНИЕ — тот же InputModeView, но только SRS-due слова (печать вместо MC)
  if (selectedMode === 'srs' && !courseId && !folderId) {
    return (
      <Layout showNav={false}>
        <InputModeView onExit={handleExit} srsOnly={true} />
      </Layout>
    );
  }

  // Exit summary for training modes (phrases, dictation, audition, blanked)
  if (exitSummary) {
    return (
      <Layout>
        <SessionComplete
          correctCount={exitSummary.correct}
          wrongCount={exitSummary.wrong}
          xpGained={0}
          onExit={handleExitSummaryClose}
          hasMore={session?.has_more}
          totalEligible={session?.total_eligible}
          cardsAnswered={exitSummary.answered}
          onContinue={() => setExitSummary(null)}
          transitionedWords={exitSummary.transitionedWords}
        />
      </Layout>
    );
  }

  // Session complete
  if (isComplete) {
    return (
      <Layout>
        <SessionComplete
          correctCount={correctCount}
          wrongCount={wrongCount}
          xpGained={totalXp}
          maxCombo={maxCombo}
          onExit={handleExit}
          hasMore={session?.has_more}
          totalEligible={session?.total_eligible}
          cardsAnswered={totalCardsAnswered}
          onContinue={handleContinue}
          transitionedWords={transitionedWords}
          courseId={courseId}
          lessonIndex={lesson}
          courseStep={selectedMode === 'input' ? 'input' : 'mc'}
          onStartStep2={courseId && lesson ? handleStartStep2 : undefined}
          onClaimReward={courseId && lesson ? handleClaimLessonReward : undefined}
          isGiftClaimed={isGiftClaimed}
          isClaiming={isClaiming}
          onNextLesson={courseId && lesson ? handleNextLesson : undefined}
        />
        {showGift && (
          <GiftModal
            lootDrop={lootDrop}
            xpGained={50}
            onClose={() => {
              setShowGift(false);
              setLootDrop(null);
            }}
          />
        )}
      </Layout>
    );
  }

  // Active session
  if (session && !isComplete) {
    const card = session.cards[currentIndex];
    const isErrorRound = card.mode === 'input' && initialCardsCount > 0 && currentIndex >= initialCardsCount;
    const remainingErrors = session.cards.length - currentIndex;
    const progress = isErrorRound
      ? 1
      : (initialCardsCount > 0 ? currentIndex / initialCardsCount : currentIndex / session.cards.length);

    return (
      <Layout showNav={false}>
        {/* Progress bar */}
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, background: 'var(--theme-bg-elevated)', zIndex: 50 }}>
          <div style={{
            height: '100%',
            background: isErrorRound ? '#F59E0B' : 'var(--theme-primary)',
            width: `${progress * 100}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Session header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 16px 0',
          marginTop: 4,
        }}>
          <button
            onClick={handleExit}
            style={{ background: 'none', border: 'none', color: 'var(--theme-text-secondary)', fontSize: 20, cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
          {isErrorRound ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              borderRadius: 12,
              padding: '3px 10px',
              color: '#F59E0B',
              fontSize: 12,
              fontWeight: 700,
            }}>
              <span>🔁 Ошибки: осталось {remainingErrors}</span>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
              {currentIndex + 1} / {initialCardsCount > 0 ? initialCardsCount : session.cards.length}
            </div>
          )}
          {/* Direction toggle — только для режимов с поддержкой RU→EN */}
          {['phrases', 'dictation', 'audition'].includes(card.mode) && (
            <button
              onClick={handleToggleDirection}
              disabled={isLoading}
              style={{
                background: 'var(--theme-bg-elevated)',
                border: '1px solid var(--theme-border)',
                borderRadius: 8,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--theme-primary)',
                cursor: 'pointer',
                letterSpacing: 0.5,
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {direction === 'original_to_translation' ? 'EN→RU' : 'RU→EN'}
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--theme-success)', fontWeight: 600 }}>
              {correctCount} ✓
            </div>
            <div style={{ fontSize: 13, color: 'var(--theme-error)', fontWeight: 600 }}>
              {wrongCount} ✗
            </div>
          </div>
        </div>

        {/* Тост "Изучено — перешло в режим Ввод" */}
        {wordMastered && (
          <div
            onClick={() => setWordMastered(false)}
            style={{
              position: 'fixed',
              top: 80,
              left: 0,
              right: 0,
              textAlign: 'center',
              zIndex: 100,
              cursor: 'pointer',
            }}
          >
            <span style={{
              display: 'inline-block',
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-primary)',
              borderRadius: 20,
              padding: '6px 16px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--theme-primary)',
              whiteSpace: 'nowrap',
              animation: 'fadeIn 0.3s ease',
              boxShadow: 'var(--theme-shadow)',
            }}>
              🎉 Изучено — перешло в режим Ввод
            </span>
          </div>
        )}

        {/* Admin edit ✏️ для не-MC карточек: лента над карточкой.
            MC рендерит свою inline-кнопку в строке POS/CEFR — там она часть карточки. */}
        {(card.mode === 'audition' || card.mode === 'dictation' || card.mode === 'phrases' || card.mode === 'blanked') && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '0 16px',
            marginBottom: -8,
            marginTop: 4,
            position: 'relative',
            zIndex: 5,
          }}>
            <AdminEditButton
              dictionaryWordId={(card.word as any).dictionary_word_id}
              userWordId={card.word.id}
              onSaved={(updated) => handleCurrentCardRegenerate(updated)}
              inline
            />
          </div>
        )}

        {/* Combo Badge */}
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: 28, marginTop: 8 }}>
          <ComboBadge combo={combo} xpFloat={xpFloat} />
        </div>

        {/* Card */}
        <div className={isCardShaking ? 'card-shake' : ''}>
          {card.mode === 'input' ? (
            <CourseInputCard
              key={`${card.word.id}_${currentIndex}`}
              card={card}
              onAnswer={handleAnswer}
              isDesktop={isDesktop}
              pendingAiFeedback={pendingAiFeedback}
              autoAdvance={autoAdvance}
              onAdvance={handleManualAdvance}
              isErrorRound={isErrorRound}
            />
          ) : card.mode === 'audition' ? (
            <AuditionCard key={card.word.id} card={card} onAnswer={(isCorrect) => handleAnswer(isCorrect)} isDesktop={isDesktop} />
          ) : card.mode === 'dictation' ? (
            <DictationCard key={card.word.id} card={card} onAnswer={handleAnswer} isDesktop={isDesktop} pendingAiFeedback={pendingAiFeedback} />
          ) : card.mode === 'phrases' ? (
            <PhraseCard key={card.word.id} card={card} onAnswer={handleAnswer} isDesktop={isDesktop} showText={phraseShowText} onToggleShowText={setPhraseShowText} />
          ) : card.mode === 'blanked' ? (
            <BlankCard key={card.word.id} card={card} onAnswer={(isCorrect) => handleAnswer(isCorrect)} isDesktop={isDesktop} />
          ) : card.mode === 'mc' || card.mode === 'srs' ? (
            <McCard
              key={`${card.word.id}_${card.direction}_${currentIndex}`}
              card={card}
              onAnswer={(isCorrect) => handleAnswer(isCorrect)}
              onManualAdvance={handleManualAdvance}
              mcReps={mcReps}
              autoAdvance={autoAdvance}
              isMastered={wordMastered}
              isDesktop={isDesktop}
              combo={combo}
              onWordEdited={handleCurrentCardRegenerate}
            />
          ) : (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--theme-text-secondary)' }}>
              Неизвестный режим: {card.mode}
            </div>
          )}
        </div>

        {/* Кнопка "Далее" при autoAdvance=false (для Dictation/Audition — не MC и не Input) */}
        {pendingAdvance && card.mode !== 'mc' && card.mode !== 'srs' && card.mode !== 'input' && (
          <div style={{
            position: 'fixed',
            bottom: 20,
            left: 16,
            right: 16,
            zIndex: 100,
          }}>
            <button
              onClick={handleManualAdvance}
              style={{
                width: '100%',
                padding: '16px',
                background: 'var(--theme-primary)',
                color: 'var(--theme-primary-text)',
                border: 'none',
                borderRadius: 12,
                fontSize: 17,
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              }}
            >
              Далее →
            </button>
          </div>
        )}

        {showGift && <GiftModal lootDrop={lootDrop} onClose={() => { setShowGift(false); setLootDrop(null); }} />}
        {toastMessage && <Toast message={toastMessage} onDone={() => setToastMessage(null)} />}
      </Layout>
    );
  }

  // Если это курс — НИКОГДА не показываем общий экран выбора тренировок словаря
  if (courseId) {
    return (
      <Layout>
        <div style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>📚</div>
          <div style={{ fontSize: 17, color: 'var(--theme-text)', fontWeight: 700, marginBottom: 8 }}>
            Загрузка урока...
          </div>
          <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)', marginBottom: 24 }}>
            Готовим карточки курса
          </div>
          <button
            onClick={() => navigate(`/courses?open=${courseId}`)}
            style={{
              padding: '12px 24px',
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 12,
              color: 'var(--theme-text)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ← Вернуться в курс
          </button>
        </div>
        {showGift && <GiftModal lootDrop={lootDrop} onClose={() => { setShowGift(false); setLootDrop(null); }} />}
        {toastMessage && <Toast message={toastMessage} onDone={() => setToastMessage(null)} />}
      </Layout>
    );
  }

  // Mode selector (ТОЛЬКО для общего словаря)
  return (
    <Layout>
      <ModeSelector
        onSelectMode={startSession}
        wordCounts={wordCounts}
        courseMode={!!courseId}
        courseName={courseName}
        folderMode={!!folderId}
        folderName={folderName}
        onNavigate={(path) => navigate(path)}
      />
      {showGift && <GiftModal lootDrop={lootDrop} onClose={() => { setShowGift(false); setLootDrop(null); }} />}
      {toastMessage && <Toast message={toastMessage} onDone={() => setToastMessage(null)} />}
    </Layout>
  );
}
